// askSmart-ui/src/pages/ManageSMRTCOP.tsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import "./ManageSMRTCOP.css";
import SMRTCOPTemplateEditor from "./SMRTCOPTemplateEditor";
import SMRTCOPAssignCrewModal from "../components/SMRTCOPAssignCrewModal";
import SMRTCOPTrafficModal, {
  getRedItems,
  getYellowItems,
  getGreenItems,
} from "../components/SMRTCOPTrafficModal";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import {
  COP_FIXED_DOCS,
  COP_ADDITIONAL_FIELD_KEY,
  COP_ADDITIONAL_ACCEPT,
  copDocLabelForKey,
  CopDocKey,
} from "./smrtcop/copDocs";
import { groupSectionPhotos } from "./smrtcop/submissionPhotos";
import type { AntennaPositionsBySector } from "./smrtcop/antennaDescriptors";
// The crew wizard itself, mounted read-only for the Wizard Viewer. Reused
// rather than reproduced so the admin always sees the tech's actual screen.
import SMRTCOP from "./SMRTCOP";
import { DownloadIcon } from "../components/DownloadIcon";
import { EyeIcon } from "./smrtcop/EyeIcon";
import {
  isFsAccessSupported,
  pickPhotoDirectory,
  ensureDirPermission,
  writeFileToDir,
  downloadFileName,
  fetchPhotoBytes,
} from "./smrtcop/photoDownload";
import {
  isTxtrptSlot,
  openTxtrptReportPdf,
  copyTxtrptText,
} from "../utils/txtrptView";
import { useAuth } from "../hooks/useAuth";
import {
  SiteHistory,
  SiteHistoryStepKey,
  deriveProgressStatus,
  buildSiteTimeline,
} from "./smrtcop/siteHistory";

// delay to block an accidental double-submit
// navigational path doesn't allow for  completion or error signal (optimistic approach)
const EXPORT_REENABLE_DELAY_MS = 2000;

// ─── Types ────────────────────────────────────────────────────────────────────

type COPQuestionType =
  | "text"
  | "textarea"
  | "number"
  | "select"
  | "checkbox"
  | "date"
  | "photo"
  | "heading"
  | "certification";

// Visibility rule for conditional display — mirrors Manage Profiles' VisibilityRule
// but is COP-local. Evaluated at wizard runtime to show/hide questions.
interface COPVisibilityRule {
  questionKey: string;
  operator: "equals" | "notEquals";
  value: string;
}

interface COPQuestion {
  key: string;
  label: string;
  helper?: string;
  type: COPQuestionType;
  required: boolean;
  options?: string[];
  // Format constraints — apply to text / number question types.
  minLength?: number;
  maxLength?: number;
  mask?: string;
  // Conditional-visibility rules; AND-combined.
  visibleWhen?: COPVisibilityRule[];
}

// Evaluate a question's "show when" rules against the submitted answers —
// mirrors isQuestionVisible() in the wizard (SMRTCOP.tsx) so the admin view
// hides conditional fields the user never triggered.
const isFieldVisible = (
  q: COPQuestion,
  data: Record<string, unknown>,
): boolean => {
  if (!q.visibleWhen || q.visibleWhen.length === 0) return true;
  return q.visibleWhen.every((rule) => {
    if (!rule.questionKey) return true;
    const raw = data[rule.questionKey];
    const actual = Array.isArray(raw) ? raw.join(",") : String(raw ?? "");
    const matches = actual === rule.value;
    return rule.operator === "equals" ? matches : !matches;
  });
};

type COPSectionType = "manual" | "bom" | "photo" | "reports" | "alignment" | "jha";

interface COPSection {
  id: number;
  label: string;
  questions: COPQuestion[];
  // Legacy templates have no type; treat any missing value as "manual".
  type?: COPSectionType;
}

// Report upload slots — mirrors REPORT_SLOTS in SMRTCOP.tsx (the wizard). The
// crew uploads these via /smrtcop/files and the persisted metadata object is
// stored at submitted[fieldKey] (an object carrying the blob name).
interface ReportFileRef {
  name: string;
  type: string;
  blobName: string;
  uploadedAt: string;
  // RET .txtrpt scans carry a sibling readable PDF stored on upload (absent for
  // scans predating PDF storage — those fall back to on-demand generation).
  pdfBlobName?: string;
  pdfName?: string;
}
// RET/RRH slots carry sector + which of the site's two RET positions ("p1" =
// first/lower, "p4" = second/higher) so labels are computed from the site's
// actual positions (e.g. "Alpha P3"), not a hardcoded P1/P4. Mirrors SMRTCOP.tsx.
interface ReportSlot { fieldKey: string; label: string; sector?: string; port?: "p1" | "p4" }
const DEFAULT_RET_POSITIONS: number[] = [1, 4];
// Usually TWO positions, but a site where only one antenna is being replaced
// needs a single RET report — "p4" is then undefined and its slots are omitted.
function retPositionsOf(meta: { retPositions?: number[] } | undefined | null): number[] {
  const p = meta?.retPositions;
  if (!Array.isArray(p)) return [...DEFAULT_RET_POSITIONS];
  const nums = p.map(Number).filter((n) => Number.isFinite(n));
  return nums.length ? nums : [...DEFAULT_RET_POSITIONS];
}
function portNumber(port: "p1" | "p4" | undefined, positions: number[]): number | undefined {
  if (port === "p1") return positions[0];
  if (port === "p4") return positions[1];
  return undefined;
}
function slotApplies(slot: { port?: "p1" | "p4" }, positions: number[]): boolean {
  return !slot.port || portNumber(slot.port, positions) !== undefined;
}
function retSlotLabel(slot: ReportSlot, positions: number[]): string {
  const n = portNumber(slot.port, positions);
  return slot.sector && n !== undefined ? `${slot.sector} P${n}` : slot.label;
}

const RET_SLOTS: ReportSlot[] = [
  { fieldKey: "ret_alpha_p1", label: "Alpha P1", sector: "Alpha", port: "p1" },
  { fieldKey: "ret_alpha_p4", label: "Alpha P4", sector: "Alpha", port: "p4" },
  { fieldKey: "ret_beta_p1", label: "Beta P1", sector: "Beta", port: "p1" },
  { fieldKey: "ret_beta_p4", label: "Beta P4", sector: "Beta", port: "p4" },
  { fieldKey: "ret_gamma_p1", label: "Gamma P1", sector: "Gamma", port: "p1" },
  { fieldKey: "ret_gamma_p4", label: "Gamma P4", sector: "Gamma", port: "p4" },
];
const RET_DELTA_SLOTS: ReportSlot[] = [
  { fieldKey: "ret_delta_p1", label: "Delta P1", sector: "Delta", port: "p1" },
  { fieldKey: "ret_delta_p4", label: "Delta P4", sector: "Delta", port: "p4" },
];
const ALIGNMENT_SLOT: ReportSlot = { fieldKey: "alignment_report", label: "Alignment Report" };

function isRetDeltaAdded(data: Record<string, any>): boolean {
  return !!data.ret_delta_added || !!data.ret_delta_p1 || !!data.ret_delta_p4;
}
// RET slots one row per sector (+ Delta when added). Grouped BY SECTOR rather
// than chunked in twos so a single-position site (one slot per sector) doesn't
// end up pairing Alpha with Beta on the same row.
function retSectorRows(deltaAdded: boolean, positions: number[]): ReportSlot[][] {
  const slots = [...RET_SLOTS, ...(deltaAdded ? RET_DELTA_SLOTS : [])].filter(
    (s) => slotApplies(s, positions),
  );
  const rows: ReportSlot[][] = [];
  const bySector = new Map<string, ReportSlot[]>();
  for (const slot of slots) {
    const key = slot.sector || slot.fieldKey;
    if (!bySector.has(key)) {
      const row: ReportSlot[] = [];
      bySector.set(key, row);
      rows.push(row);
    }
    bySector.get(key)!.push(slot);
  }
  return rows;
}
// RRH serial photo sectors + their 6 device slots (2 ports × 3 devices).
const RRH_SECTORS: { key: string; label: string }[] = [
  { key: "alpha", label: "Alpha" },
  { key: "beta", label: "Beta" },
  { key: "gamma", label: "Gamma" },
];
interface RrhSlot { fieldKey: string; port: "p1" | "p4"; n: number }
// 3 devices per RET position — 6 on a two-position site, 3 on a single.
function rrhSlotsForSector(sectorKey: string, positions?: number[]): RrhSlot[] {
  const slots: RrhSlot[] = [];
  (["p1", "p4"] as const).forEach((port) => {
    if (positions && portNumber(port, positions) === undefined) return;
    for (let n = 1; n <= 3; n++) {
      slots.push({ fieldKey: `rrh_${sectorKey}_${port}_${n}`, port, n });
    }
  });
  return slots;
}
function rrhSlotLabel(slot: RrhSlot, positions: [number, number]): string {
  return `P${portNumber(slot.port, positions)} #${slot.n}`;
}
function rrhSectorsList(deltaAdded: boolean): { key: string; label: string }[] {
  return deltaAdded ? [...RRH_SECTORS, { key: "delta", label: "Delta" }] : [...RRH_SECTORS];
}

// Human label for an expanded photo slot key. The wizard multiplies a single
// photo question into per-sector/antenna/radio/SPD and per-verification-line
// keys; we reconstruct a readable label from the suffix appended to the base
// question key (see expandPhotoQuestion / verification photos in SMRTCOP.tsx).
const photoSlotLabel = (baseLabel: string, suffix: string): string => {
  if (!suffix) return baseLabel;
  const sector = suffix.match(/^__sector_([A-C])_(antenna|radio)_(\d+)$/);
  if (sector) {
    const item = sector[2][0].toUpperCase() + sector[2].slice(1);
    return `${baseLabel} — Sector ${sector[1]} – ${item} ${sector[3]}`;
  }
  const spd = suffix.match(/^__spd_(\d+)$/);
  if (spd) return `${baseLabel} — SPD ${spd[1]}`;
  const photo = suffix.match(/^_photo_(\d+)$/);
  if (photo) return `${baseLabel} — Photo ${photo[1]}`;
  return `${baseLabel} ${suffix.replace(/^_+/, "").replace(/_/g, " ")}`.trim();
};

interface COPTemplate {
  id: string;
  name: string;
  version: number;
  status: "draft" | "active";
  sections: COPSection[];
  createdAt: string;
  updatedAt: string;
}

type COPIngestSlot = "fixed" | "additional";

interface COPIngestFile {
  name: string;
  previewUrl: string; // object URL for new files; "" for edit-mode files with no cached File
  type: string;
  size: number;
  fileObj?: File; // kept in memory only — not persisted to localStorage
  slot: COPIngestSlot;
  docKey?: CopDocKey; // populated when slot === "fixed"
  docLabel?: string; // populated when slot === "fixed"
  blobName?: string;
}

interface COPSourceFile {
  name: string;
  type: string;
  blobName?: string;
  slot?: COPIngestSlot; // legacy records may omit; treat missing as "additional"
  docKey?: CopDocKey;
  docLabel?: string;
}

interface COPAzimuths {
  A: string;
  B: string;
  C: string;
  // Optional fourth-sector azimuth. Present (even as "") once the verifier
  // adds a Delta sector via the "+" control; absent otherwise. Feeds the RET
  // output's Orientation column for Delta devices.
  D?: string;
}

// Controlling RET (the RRH that controls the RET) per sector, one value per
// port (P1 / P4). Sourced from the RFDS "Cell Details Final" sheet and verified
// by the admin at ingestion; feeds the RET output's "Radio head controlling
// RET" column, where the same value repeats across all devices of a
// sector + port. Delta (D) is present only when the site has a Delta sector.
interface COPControllingRet {
  p1: string;
  p4: string;
}
interface COPControllingRets {
  A: COPControllingRet;
  B: COPControllingRet;
  C: COPControllingRet;
  D?: COPControllingRet;
}

interface COPExtractedSiteData {
  siteId: string;
  siteName: string;
  faLocationCode: string;
  usid: string;
  iwm: string; // from Construction Drawings page-1 header "IWM/ PTN:" (1st token; == JHA PACE NUMBER)
  ptn: string; // from Construction Drawings page-1 header "IWM/ PTN:" (2nd token)
  market: string;
  financeRegion: string;
  financeSubmarket: string;
  projectName: string;
  address: string;
  city: string;
  county: string;
  state: string;
  zip: string;
  coordinates: string;
  azimuths: COPAzimuths;
  controllingRets: COPControllingRets;
  // The two antenna positions the site's RET scans cover (e.g. [1, 4] or
  // [1, 3]). Derived from the RFDS CellDetails(Final) 700-band rows — NOT
  // hardcoded P1/P4. Drives the RET slot labels and the output's Column L.
  retPositions: number[];
  // USEIDs with Status=NEW from the RFDS CellDetails(Final), grouped by sector
  // (A/B/C/D). Verify tech reviews these; feeds the AN204 alignment upload.
  newUseIdsBySector: Record<string, string[]>;
  // Model + linked cells per antenna column, from the RFDS "Antenna Position
  // Details - Sector X (Final)" pages. Feeds the tape drop Model/Technology
  // dropdown, which used to carry a static option list.
  antennaPositionsBySector: AntennaPositionsBySector;
  scopeOfWork: string;
}

interface COPSiteMeta {
  siteId: string;
  siteName: string;
  faLocationCode: string;
  usid: string;
  iwm: string; // from Construction Drawings page-1 header "IWM/ PTN:" (1st token; == JHA PACE NUMBER)
  ptn: string; // from Construction Drawings page-1 header "IWM/ PTN:" (2nd token)
  market: string;
  financeRegion: string;
  financeSubmarket: string;
  projectName: string;
  address: string;
  city: string;
  county: string;
  state: string;
  zip: string;
  coordinates: string;
  azimuths: COPAzimuths;
  controllingRets: COPControllingRets;
  // The two antenna positions the site's RET scans cover (e.g. [1, 4] or
  // [1, 3]). Derived from the RFDS CellDetails(Final) 700-band rows — NOT
  // hardcoded P1/P4. Drives the RET slot labels and the output's Column L.
  retPositions: number[];
  // USEIDs with Status=NEW from the RFDS CellDetails(Final), grouped by sector
  // (A/B/C/D). Verify tech reviews these; feeds the AN204 alignment upload.
  newUseIdsBySector: Record<string, string[]>;
  // See ExtractedSiteData above — persisted on the site so the crew wizard can
  // build the tape drop dropdown without re-reading the RFDS.
  antennaPositionsBySector: AntennaPositionsBySector;
  scopeOfWork: string;
  sourceFiles: COPSourceFile[];
}

interface COPSite {
  id: string;
  name: string;
  clientId: string;
  templateId: string;
  // All assigned crew (crew lead + tower crew). The crew lead is kept first.
  assignedUserIds: string[];
  // The single crew lead for this site (the only assignee who may certify).
  // Older sites predate this field — fall back to assignedUserIds[0].
  crewLeadId?: string;
  // id → display name for every assigned crew member, stamped at assign time so
  // the crew wizard (which has no org user list) can show names for per-step
  // assignment. Kept in sync whenever the crew is (re)assigned.
  crewNames?: Record<string, string>;
  // Per-wizard-step assignment: stepId (section.id as string) → assignee userId.
  // Lead-managed in the field; drives the per-step read-only lock. Absent = open.
  stepAssignments?: Record<string, string>;
  status:
    | "Review Now"
    | "In Progress"
    | "Assign Crew"
    | "Active"
    | "Complete"
    | "Submitted"
    | "Approved"
    | "Archived";
  createdAt: string;
  updatedAt: string;
  meta: COPSiteMeta;
  // Progression history (step → { at, by, byName }); drives Status History.
  history?: SiteHistory;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const BLANK_AZIMUTHS: COPAzimuths = { A: "", B: "", C: "" };

const blankControllingRets = (): COPControllingRets => ({
  A: { p1: "", p4: "" },
  B: { p1: "", p4: "" },
  C: { p1: "", p4: "" },
});

const BLANK_EXTRACTED: COPExtractedSiteData = {
  siteId: "",
  siteName: "",
  faLocationCode: "",
  usid: "",
  iwm: "",
  ptn: "",
  market: "",
  financeRegion: "",
  financeSubmarket: "",
  projectName: "",
  address: "",
  city: "",
  county: "",
  state: "",
  zip: "",
  coordinates: "",
  azimuths: { ...BLANK_AZIMUTHS },
  controllingRets: blankControllingRets(),
  retPositions: [...DEFAULT_RET_POSITIONS],
  newUseIdsBySector: {},
  antennaPositionsBySector: {},
  scopeOfWork: "",
};

// Tolerate legacy site records whose azimuths is a joined string ("A: 70, B: 190, C: 310"
// or "0, 120, 240"). Missing or non-object values become an empty {A,B,C}.
function normalizeAzimuths(raw: unknown): COPAzimuths {
  if (raw && typeof raw === "object") {
    const r = raw as Partial<COPAzimuths>;
    return {
      A: r.A ?? "",
      B: r.B ?? "",
      C: r.C ?? "",
      // Preserve Delta only when the record actually carries it.
      ...(r.D !== undefined ? { D: r.D } : {}),
    };
  }
  return { ...BLANK_AZIMUTHS };
}

// Tolerate legacy records that predate Controlling RET (missing → blank set).
function normalizeControllingRet(raw: unknown): COPControllingRet {
  if (raw && typeof raw === "object") {
    const r = raw as Partial<COPControllingRet>;
    return { p1: r.p1 ?? "", p4: r.p4 ?? "" };
  }
  return { p1: "", p4: "" };
}
function normalizeControllingRets(raw: unknown): COPControllingRets {
  const r = (raw && typeof raw === "object" ? raw : {}) as Partial<COPControllingRets>;
  return {
    A: normalizeControllingRet(r.A),
    B: normalizeControllingRet(r.B),
    C: normalizeControllingRet(r.C),
    ...(r.D !== undefined ? { D: normalizeControllingRet(r.D) } : {}),
  };
}

// The site's antenna positions, ascending and de-duplicated. Usually two, but
// ONE when only a single antenna is being replaced (see the RFDS antenna-change
// rule in smrtcopOcr.ts). Falls back to [1,4] for legacy/missing values.
function normalizeRetPositions(raw: unknown): number[] {
  if (Array.isArray(raw)) {
    const nums = raw.map(Number).filter((n) => Number.isFinite(n));
    const uniq = Array.from(new Set(nums)).sort((a, b) => a - b);
    if (uniq.length) return uniq.slice(0, 2);
  }
  return [...DEFAULT_RET_POSITIONS];
}

// RET and Alignment are two independently orderable template sections. Older
// templates carry a single "reports" (RET) section only; inject an Alignment
// section right after it so it shows in the editor (reorderable/renamable) and
// in the site View. Deterministic (nextId = max(ids)+1) so the injected id
// matches the wizard's own ensureAlignmentSection — reveal/step ids line up
// even before the migrated template is re-saved.
function ensureAlignmentSection(sections: COPSection[]): COPSection[] {
  if (sections.some((s) => s.type === "alignment")) return sections;
  const reportsIdx = sections.findIndex((s) => s.type === "reports");
  if (reportsIdx === -1) return sections;
  const nextId = sections.length
    ? Math.max(...sections.map((s) => s.id)) + 1
    : 1;
  const out = [...sections];
  out.splice(reportsIdx + 1, 0, {
    id: nextId,
    label: "Alignment Report",
    questions: [],
    type: "alignment",
  });
  return out;
}

function migrateTemplateAlignment(tpl: COPTemplate): COPTemplate {
  const sections = ensureAlignmentSection(tpl.sections);
  return sections === tpl.sections ? tpl : { ...tpl, sections };
}

// JHA day slots — mirrors the wizard: 5 by default, more if `jha_day_count`
// was bumped. Keyed `jha_day_{n}`, labelled "Day n".
const JHA_MIN_DAYS = 5;
function jhaDayCount(data: Record<string, any>): number {
  const n = Number(data?.jha_day_count);
  return Number.isFinite(n) && n > JHA_MIN_DAYS ? Math.floor(n) : JHA_MIN_DAYS;
}
function jhaDaySlots(data: Record<string, any>): { fieldKey: string; label: string }[] {
  return Array.from({ length: jhaDayCount(data) }, (_, i) => ({
    fieldKey: `jha_day_${i + 1}`,
    label: `Day ${i + 1}`,
  }));
}

function buildCOPSite(
  data: COPExtractedSiteData,
  files: COPIngestFile[],
  id: string,
): COPSite {
  return {
    id,
    name: data.siteId,
    clientId: "",
    templateId: "",
    assignedUserIds: [],
    status: "Review Now",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    meta: {
      siteId: data.siteId,
      siteName: data.siteName,
      faLocationCode: data.faLocationCode,
      usid: data.usid,
      iwm: data.iwm,
      ptn: data.ptn,
      market: data.market,
      financeRegion: data.financeRegion,
      financeSubmarket: data.financeSubmarket,
      projectName: data.projectName,
      address: data.address,
      city: data.city,
      county: data.county,
      state: data.state,
      zip: data.zip,
      coordinates: data.coordinates,
      azimuths: data.azimuths,
      controllingRets: data.controllingRets,
      retPositions: data.retPositions,
      newUseIdsBySector: data.newUseIdsBySector,
      antennaPositionsBySector: data.antennaPositionsBySector,
      scopeOfWork: data.scopeOfWork,
      sourceFiles: files.map((f) => ({
        name: f.name,
        type: f.type,
        slot: f.slot,
        docKey: f.docKey,
        docLabel: f.docLabel,
        blobName: f.blobName,
      })),
    },
  };
}

function newCOPTemplate(n: number): COPTemplate {
  return {
    id: `tpl_${Date.now()}`,
    name: `Template ${n}`,
    version: 1,
    status: "draft",
    sections: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ManageSMRTCOP() {
  const { user } = useAuth();
  // ── Top-level tab ─────────────────────────────────────────────────────────
  const [activeTopTab, setActiveTopTab] = useState<
    "overview" | "approved" | "template" | "log" | "rules"
  >("overview");
  // Manually built templates live on a second row behind a triangle, so a
  // long template list can't push the fixed tabs off the page. The default
  // "3 Sector" template stays inline — it's the one every org has.
  const [templateTabsOpen, setTemplateTabsOpen] = useState(false);
  // Which year's sub-tab is open under Approved. Null until the sites load,
  // then defaults to the most recent year that actually has approvals.
  const [approvedYear, setApprovedYear] = useState<string | null>(null);
  // Site whose Reopen Approval modal is open, with the mandatory reason.
  const [reopenApprovalSite, setReopenApprovalSite] = useState<COPSite | null>(
    null,
  );
  const [reopenApprovalReason, setReopenApprovalReason] = useState("");
  const [reopenApprovalBusy, setReopenApprovalBusy] = useState(false);
  // Site whose Status History modal is open (null = closed).
  const [historySite, setHistorySite] = useState<COPSite | null>(null);
  // Site whose crew wizard is open in the read-only Wizard Viewer.
  const [wizardViewerSiteId, setWizardViewerSiteId] = useState<string | null>(
    null,
  );

  // ── Log (rejections + approval events) ─────────────────────────────────────
  interface RejectionEvent {
    id: string;
    siteId: string;
    siteName: string;
    fieldKey: string;
    category: string;
    reason: string;
    source: "admin" | "system";
    rejectedByUserId: string;
    rejectedByName: string;
    sentToUserId: string;
    sentToName: string;
    at: string;
  }
  // Site lifecycle events that aren't rejections — client approval granted, and
  // an admin reopening it. Served by GET /smrtcop/log off the same org doc.
  interface SiteLogEvent {
    id: string;
    siteId: string;
    siteName: string;
    kind: "approved" | "approval_reopened";
    reason: string;
    byUserId: string;
    byName: string;
    at: string;
  }

  // One row shape for the merged feed, so the table renders a single list
  // rather than branching per source at every cell.
  interface LogRow {
    id: string;
    at: string;
    event: string;
    siteLabel: string;
    by: string;
    sentTo: string;
    item: string;
    detail: string;
    tone: "reject" | "approve" | "reopen";
    system: boolean;
  }

  const [rejectionEvents, setRejectionEvents] = useState<RejectionEvent[]>([]);
  const [siteLogEvents, setSiteLogEvents] = useState<SiteLogEvent[]>([]);
  const [rejectionsLoading, setRejectionsLoading] = useState(false);
  const [rejectionsError, setRejectionsError] = useState<string | null>(null);
  // Bumped after a reopen so the Log tab refetches the entry that just landed.
  const [logRefresh, setLogRefresh] = useState(0);

  useEffect(() => {
    if (activeTopTab !== "log") return;
    setRejectionsLoading(true);
    setRejectionsError(null);
    const get = (path: string) =>
      fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtcop/${path}`, {
        credentials: "include",
      }).then((r) => {
        if (!r.ok) throw new Error(`Failed to load (${r.status})`);
        return r.json();
      });
    Promise.all([get("rejections"), get("log")])
      .then(([rejections, events]) => {
        setRejectionEvents(Array.isArray(rejections) ? rejections : []);
        setSiteLogEvents(Array.isArray(events) ? events : []);
      })
      .catch((err) => setRejectionsError(err?.message || "Failed to load log"))
      .finally(() => setRejectionsLoading(false));
  }, [activeTopTab, logRefresh]);

  // Merge both sources into one reverse-chronological feed.
  const logRows: LogRow[] = React.useMemo(() => {
    const rows: LogRow[] = [
      ...rejectionEvents.map((e) => ({
        id: `r_${e.id}`,
        at: e.at,
        event: e.source === "system" ? "Rejected (rule)" : "Rejected",
        siteLabel: e.siteName || e.siteId,
        by: e.rejectedByName || e.rejectedByUserId || "—",
        sentTo: e.sentToName || e.sentToUserId || "—",
        item: e.category || "—",
        detail: e.reason || "—",
        tone: "reject" as const,
        system: e.source === "system",
      })),
      ...siteLogEvents.map((e) => ({
        id: `s_${e.id}`,
        at: e.at,
        event: e.kind === "approved" ? "Client Approved" : "Approval Reopened",
        siteLabel: e.siteName || e.siteId,
        by: e.byName || e.byUserId || "—",
        sentTo: "—",
        item: e.kind === "approved" ? "Site Approval" : "Site Approval",
        detail:
          e.reason ||
          (e.kind === "approved"
            ? "Client approval confirmed via SMRT Task."
            : "—"),
        tone: e.kind === "approved" ? ("approve" as const) : ("reopen" as const),
        system: false,
      })),
    ];
    rows.sort((a, b) => (a.at < b.at ? 1 : -1));
    return rows;
  }, [rejectionEvents, siteLogEvents]);

  // ── Alignment tool calibration scan ────────────────────────────────────────
  // Re-reads every stored Antenna Alignment Report, seeds the tool registry and
  // raises tasks. Runs server-side (managed identity) rather than from a laptop,
  // so no Cosmos/storage firewall rules are involved.
  interface ScanResult {
    apply: boolean;
    reports: number;
    rows: Array<{ site: string; serial: string; due: string; status: string }>;
    tools: Array<{
      serial: string;
      due: string;
      site: string;
      lead: string;
      usedOn: string;
    }>;
    unreadable: number;
    firstError: string;
    aborted: string;
    evaluation: Array<{
      serial: string;
      due: string;
      days: number;
      action: string;
    }>;
  }
  const [scanning, setScanning] = useState<"" | "preview" | "apply">("");
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  const runToolScan = async (apply: boolean) => {
    setScanning(apply ? "apply" : "preview");
    setScanError(null);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/tool-calibration/scan`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ apply }),
        },
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.error || `Scan failed (${r.status})`);
      }
      setScanResult(await r.json());
    } catch (err) {
      setScanError((err as Error).message || "Scan failed");
      setScanResult(null);
    } finally {
      setScanning("");
    }
  };

  // ── Rules registry (read-only) ─────────────────────────────────────────────
  interface RuleDef {
    id: string;
    name: string;
    category: string;
    appliesTo: string;
    description: string;
    action: string;
  }
  const [rules, setRules] = useState<RuleDef[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState<string | null>(null);

  useEffect(() => {
    if (activeTopTab !== "rules") return;
    setRulesLoading(true);
    setRulesError(null);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/smrtcop/rules`, {
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load (${r.status})`);
        return r.json();
      })
      .then((data) => setRules(Array.isArray(data) ? data : []))
      .catch((err) => setRulesError(err?.message || "Failed to load rules"))
      .finally(() => setRulesLoading(false));
  }, [activeTopTab]);

  // ── Sites ─────────────────────────────────────────────────────────────────
  const [sites, setSites] = useState<COPSite[]>([]);

  // ── Templates ─────────────────────────────────────────────────────────────
  const [templates, setTemplates] = useState<COPTemplate[]>([]);

  // Counts in-flight per-item writes so the background poll won't revert an
  // optimistic local change before the server has confirmed it.
  const writesInFlightRef = useRef(0);

  // Poll guard refs — the load/sync effect runs with []-deps and can't read
  // fresh editing state directly. Kept in sync just below the state block.
  const activeTopTabRef = useRef(activeTopTab);
  const editingSectionIdRef = useRef<number | null>(null);
  const renamingTabIdRef = useRef<string | null>(null);
  const newTemplateModalRef = useRef(false);

  // ── Remote persistence (per-item, concurrency-safe) ───────────────────────
  // Every site/template change is pushed as a single-item upsert/delete so two
  // admins editing different items can never overwrite each other (the backend
  // does a read-modify-write under an ETag). This replaces the old debounced
  // whole-array PUT that let a stale client wipe the list.
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const putSiteRemote = useCallback(
    async (site: COPSite) => {
      writesInFlightRef.current++;
      try {
        const res = await fetch(
          `${apiBase}/smrtcop/sites/${encodeURIComponent(site.id)}`,
          {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ site }),
          },
        );
        if (!res.ok) console.error("Failed to save site:", res.status);
      } catch (err) {
        console.error("Failed to save site:", err);
      } finally {
        writesInFlightRef.current--;
      }
    },
    [apiBase],
  );

  const deleteSiteRemote = useCallback(
    async (siteId: string) => {
      writesInFlightRef.current++;
      try {
        await fetch(`${apiBase}/smrtcop/sites/${encodeURIComponent(siteId)}`, {
          method: "DELETE",
          credentials: "include",
        });
      } catch (err) {
        console.warn("Site delete failed:", err);
      } finally {
        writesInFlightRef.current--;
      }
    },
    [apiBase],
  );

  const putTemplateRemote = useCallback(
    async (tpl: COPTemplate) => {
      writesInFlightRef.current++;
      try {
        const res = await fetch(
          `${apiBase}/smrtcop/templates/${encodeURIComponent(tpl.id)}`,
          {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ template: tpl }),
          },
        );
        if (!res.ok) console.error("Failed to save template:", res.status);
      } catch (err) {
        console.error("Failed to save template:", err);
      } finally {
        writesInFlightRef.current--;
      }
    },
    [apiBase],
  );

  const deleteTemplateRemote = useCallback(
    async (templateId: string) => {
      writesInFlightRef.current++;
      try {
        await fetch(
          `${apiBase}/smrtcop/templates/${encodeURIComponent(templateId)}`,
          { method: "DELETE", credentials: "include" },
        );
      } catch (err) {
        console.warn("Template delete failed:", err);
      } finally {
        writesInFlightRef.current--;
      }
    },
    [apiBase],
  );

  // Section edits fire on every keystroke, so debounce per template id.
  const templateSaveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const scheduleTemplateSave = useCallback(
    (tpl: COPTemplate) => {
      const timers = templateSaveTimers.current;
      const existing = timers.get(tpl.id);
      if (existing) clearTimeout(existing);
      timers.set(
        tpl.id,
        setTimeout(() => {
          timers.delete(tpl.id);
          putTemplateRemote(tpl);
        }, 800),
      );
    },
    [putTemplateRemote],
  );
  const cancelTemplateSave = useCallback((templateId: string) => {
    const t = templateSaveTimers.current.get(templateId);
    if (t) {
      clearTimeout(t);
      templateSaveTimers.current.delete(templateId);
    }
  }, []);

  // ── Load + background sync ────────────────────────────────────────────────
  const lastSitesHash = useRef("");
  const lastTemplatesHash = useRef("");
  useEffect(() => {
    const fetchData = async () => {
      let sitesRes: Response;
      let tplRes: Response;
      try {
        [sitesRes, tplRes] = await Promise.all([
          fetch(`${apiBase}/smrtcop/sites`, { credentials: "include" }),
          fetch(`${apiBase}/smrtcop/templates`, { credentials: "include" }),
        ]);
      } catch (err) {
        console.error("Failed to load SMRT COP data:", err);
        return;
      }
      // Never blank out local state on a failed/expired-session response (the
      // global fetch interceptor already retried after a refresh). Skipping the
      // tick is what kills the old empty-overwrite data-loss path.
      if (!sitesRes.ok || !tplRes.ok) return;
      // Don't yank an optimistic local change while its save is in flight.
      if (writesInFlightRef.current > 0) return;

      const sitesData = await sitesRes.json().catch(() => null);
      const tplData = await tplRes.json().catch(() => null);
      if (!sitesData || !tplData) return;

      const newSites: COPSite[] = Array.isArray(sitesData.sites)
        ? sitesData.sites
        : [];
      const newTemplates: COPTemplate[] = (
        Array.isArray(tplData.templates) ? tplData.templates : []
      ).map(migrateTemplateAlignment);

      const sitesHash = JSON.stringify(newSites);
      if (sitesHash !== lastSitesHash.current) {
        setSites(newSites);
        lastSitesHash.current = sitesHash;
      }

      // Adopt server templates only when NOT actively editing one — otherwise a
      // poll would clobber in-progress section edits. This also fixes the old
      // "templates load once and never refresh" staleness that let a long-open
      // tab overwrite a template added elsewhere.
      const editingTemplate =
        activeTopTabRef.current === "template" ||
        editingSectionIdRef.current !== null ||
        renamingTabIdRef.current !== null ||
        newTemplateModalRef.current;
      const tplHash = JSON.stringify(newTemplates);
      if (!editingTemplate && tplHash !== lastTemplatesHash.current) {
        setTemplates(newTemplates);
        lastTemplatesHash.current = tplHash;
      }
    };
    fetchData();
    let interval = setInterval(fetchData, 12000);
    const onVisibility = () => {
      clearInterval(interval);
      if (document.visibilityState === "visible") {
        fetchData();
        interval = setInterval(fetchData, 12000);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [apiBase]);
  const [activeTemplateIndex, setActiveTemplateIndex] = useState<number | null>(
    null,
  );
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const [templateDirty, setTemplateDirty] = useState(false);
  const [templateSnapshot, setTemplateSnapshot] = useState<COPTemplate | null>(
    null,
  );
  const [renamingTabId, setRenamingTabId] = useState<string | null>(null);
  const [renamingTabValue, setRenamingTabValue] = useState("");
  const [showNewTemplateModal, setShowNewTemplateModal] = useState(false);
  const [duplicateSourceId, setDuplicateSourceId] = useState<string>("");
  const [viewingTemplate, setViewingTemplate] = useState<COPTemplate | null>(
    null,
  );
  const [viewingSite, setViewingSite] = useState<COPSite | null>(null);
  // Per-photo clipboard-copy status (keyed by photo key) — drives the
  // Copy/Copied/Failed label on the RET TXTRPT copy-to-clipboard buttons.
  const [copyStatus, setCopyStatus] = useState<
    Record<string, "copying" | "done" | "error">
  >({});
  // ── Photo download-to-folder (Ericsson portal blocks clipboard paste) ──────
  // Mirrors the crew View Submission: per-photo write status + a persisted ✓ set
  // + a session folder handle per site. See SMRTCOP.tsx / photoDownload.ts.
  const [downloadStatus, setDownloadStatus] = useState<
    Record<string, "saving" | "error">
  >({});
  const [siteDownloaded, setSiteDownloaded] = useState<Set<string>>(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const dirHandleRef = useRef<{ siteId: string; handle: any } | null>(null);
  // BOM-output (Ericsson .xlsx) generation status for the view screen.
  const [bomGenStatus, setBomGenStatus] = useState<
    "idle" | "generating" | "error"
  >("idle");
  const [tapeGenStatus, setTapeGenStatus] = useState<
    "idle" | "generating" | "error"
  >("idle");
  const [retGenStatus, setRetGenStatus] = useState<
    "idle" | "generating" | "error"
  >("idle");
  const [rg123GenStatus, setRg123GenStatus] = useState<
    "idle" | "generating" | "error"
  >("idle");
  const [an204GenStatus, setAn204GenStatus] = useState<
    "idle" | "generating" | "error"
  >("idle");
  // Section cards on the admin View screen default to COLLAPSED (header +
  // generate/sent footer only). Ids present here are the ones expanded.
  const [expandedSections, setExpandedSections] = useState<Set<number>>(
    new Set(),
  );
  const toggleSection = (id: number) =>
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  // Reject-to-resubmit: admin flags a single uploaded item (photo / TXTRPT) for
  // resubmission with a reason. Writes the `${key}_status`/`${key}_statusReason`
  // sidecar the crew badge reads. `rejectModal.key` is the field being rejected.
  const [rejectModal, setRejectModal] = useState<{ key: string } | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<{
    title: string;
    message: string;
    /** A refusal rather than a question — one dismiss button, no confirm. */
    blocked?: boolean;
    onConfirm: () => void;
  } | null>(null);
  const [verifyConfirm, setVerifyConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
    infoOnly?: boolean;
  } | null>(null);
  const [certHistoryOpen, setCertHistoryOpen] = useState(false);
  const [viewingSiteState, setViewingSiteState] = useState<{
    answers: Record<string, any>;
    submitted: Record<string, any>;
    portalUploads: Record<string, any>;
  }>({ answers: {}, submitted: {}, portalUploads: {} });

  // Keep the poll's guard refs in sync with editing state.
  useEffect(() => {
    activeTopTabRef.current = activeTopTab;
    editingSectionIdRef.current = editingSectionId;
    renamingTabIdRef.current = renamingTabId;
    newTemplateModalRef.current = showNewTemplateModal;
  }, [activeTopTab, editingSectionId, renamingTabId, showNewTemplateModal]);

  // Fetch site state when viewing a site
  useEffect(() => {
    if (!viewingSite) {
      setViewingSiteState({ answers: {}, submitted: {}, portalUploads: {} });
      setSiteDownloaded(new Set());
      setDownloadStatus({});
      return;
    }
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/smrtcop/site-state/${encodeURIComponent(viewingSite.id)}`,
      { credentials: "include" },
    )
      .then((r) => r.json())
      .then((data) => {
        setViewingSiteState({
          answers: data.answers || {},
          submitted: data.submitted || {},
          portalUploads: data.portalUploads || {},
        });
        // Restore the admin's own ✓ markers (kept separate from the crew's).
        setSiteDownloaded(new Set<string>(data.downloadedPhotos?.admin || []));
        setDownloadStatus({});
        // A submitted site renders against the template SNAPSHOT frozen at
        // sign-off, so later template edits never change what was submitted.
        // Non-submitted sites have no snapshot → keep the live template.
        const snap = data.templateSnapshot;
        if (snap && Array.isArray(snap.sections) && snap.sections.length) {
          setViewingTemplate(
            migrateTemplateAlignment(JSON.parse(JSON.stringify(snap))),
          );
        }
      })
      .catch((err) => console.error("Failed to load site state:", err));
  }, [viewingSite]);

  // ── Ingestion ─────────────────────────────────────────────────────────────
  const [isIngesting, setIsIngesting] = useState(false);
  const [ingestMode, setIngestMode] = useState<"create" | "edit">("create");
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [ingestFiles, setIngestFiles] = useState<COPIngestFile[]>([]);
  const [activeDocumentIndex, setActiveDocumentIndex] = useState(0);
  const [extractedSiteData, setExtractedSiteData] =
    useState<COPExtractedSiteData | null>(null);
  const [thumbCollapsed, setThumbCollapsed] = useState(false);
  const [ingestLocked, setIngestLocked] = useState(false);
  // True while the RFDS is being OCR-extracted (several seconds) — drives an
  // inline "Reading RFDS…" spinner so the user knows work is in progress.
  const [rfdsExtracting, setRfdsExtracting] = useState(false);
  // Guard concurrent Save/Verify clicks — handleSaveSite is async (uploads + PUT)
  // and can take several seconds. Setting this disables both buttons and drives
  // the spinner state while the save is in flight.
  const [isVerifying, setIsVerifying] = useState(false);
  // Flips true while handleEditSite is hydrating per-file blobs from storage;
  // drives a "Loading files…" overlay on the ingest viewer panel.
  const [isLoadingSiteFiles, setIsLoadingSiteFiles] = useState(false);

  const [isTrafficModalOpen, setIsTrafficModalOpen] = useState(false);
  const [trafficFilter, setTrafficFilter] = useState<
    "red" | "yellow" | "green" | null
  >(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningSiteId, setAssigningSiteId] = useState<string | null>(null);
  // Site id whose row has its secondary actions expanded via the ⋮ toggle.
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const [users, setUsers] = useState<any[]>([]);

  useEffect(() => {
    Promise.all([
      fetch(`${process.env.REACT_APP_BACKEND_URL}/approved_users`, {
        method: "GET",
        credentials: "include",
      }).then((r) => r.json()),
      fetch(`${process.env.REACT_APP_BACKEND_URL}/roles?scope=all`, {
        method: "GET",
        credentials: "include",
      }).then((r) => r.json()),
    ])
      .then(([data, rolesData]) => {
        const orgRoles = rolesData?.org || [];
        const copRoleIDs = new Set(
          orgRoles
            .filter(
              (r: any) =>
                r.permissions?.includes("cop.view") ||
                r.permissions?.includes("cop.admin"),
            )
            .map((r: any) => r.id),
        );
        const filtered = (Array.isArray(data) ? data : []).filter((u: any) =>
          (u.customRoles || []).some((rid: string) => copRoleIDs.has(rid)),
        );
        setUsers(
          filtered.map((u: any) => ({
            id: u.user_id || u.id || u.email,
            name: u.name || u.email,
          })),
        );
      })
      .catch((err) => console.error("Failed to load users", err));
  }, []);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const objectUrlsRef = useRef<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Caches File objects by siteId so edit mode can recreate previewUrls in-session
  const siteFilesRef = useRef<Map<string, File[]>>(new Map());

  // ── Object URL helpers ────────────────────────────────────────────────────

  const revokeAllUrls = () => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current = [];
  };

  // ── Template mutations ────────────────────────────────────────────────────

  const mutateActiveTemplate = (fn: (tpl: COPTemplate) => COPTemplate) => {
    if (activeTemplateIndex === null) return;
    const updated = [...templates];
    const edited = fn(JSON.parse(JSON.stringify(updated[activeTemplateIndex])));
    updated[activeTemplateIndex] = edited;
    setTemplates(updated);
    setTemplateDirty(true);
    scheduleTemplateSave(edited);
  };

  const handleAddTemplate = () => {
    setDuplicateSourceId("");
    setShowNewTemplateModal(true);
  };

  const handleCreateBlankTemplate = () => {
    const tpl = newCOPTemplate(templates.length + 1);
    const updated = [...templates, tpl];
    setTemplates(updated);
    setActiveTemplateIndex(updated.length - 1);
    setActiveTopTab("template");
    // The new template lands on the hidden row — open it, or the tab being
    // edited is invisible.
    setTemplateTabsOpen(true);
    setEditingSectionId(null);
    setTemplateDirty(false);
    setRenamingTabId(null);
    setShowNewTemplateModal(false);
    putTemplateRemote(tpl);
  };

  const handleDuplicateTemplate = () => {
    const source = templates.find((t) => t.id === duplicateSourceId);
    if (!source) return;
    const clone: COPTemplate = {
      ...JSON.parse(JSON.stringify(source)),
      id: `tpl_${Date.now()}`,
      name: `${source.name} Copy`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const updated = [...templates, clone];
    setTemplates(updated);
    setActiveTemplateIndex(updated.length - 1);
    setTemplateTabsOpen(true);
    setActiveTopTab("template");
    setEditingSectionId(null);
    setTemplateDirty(false);
    setRenamingTabId(null);
    setShowNewTemplateModal(false);
    putTemplateRemote(clone);
  };

  const saveTabRename = (i: number) => {
    const name = renamingTabValue.trim() || templates[i]?.name;
    if (!name) return;
    const renamed = { ...templates[i], name };
    const updated = templates.map((t, idx) => (idx === i ? renamed : t));
    setTemplates(updated);
    setRenamingTabId(null);
    putTemplateRemote(renamed);
  };

  /**
   * The sites built on a template — what stops it being deleted.
   *
   * A site reads its checklist through `templateId` on every render, so removing
   * the template leaves the wizard with no sections to show and the site
   * unopenable. A SUBMITTED site froze its own `templateSnapshot` and would
   * survive, but it reverts to the live template the moment it is reopened, so
   * it is counted here too rather than treated as safe.
   */
  const sitesUsingTemplate = (templateId: string): COPSite[] =>
    templateId ? sites.filter((site) => site.templateId === templateId) : [];

  const handleDeleteTemplate = (i: number) => {
    const tpl = templates[i];
    const inUse = tpl ? sitesUsingTemplate(tpl.id) : [];

    // Refused, not confirmed. The message names the sites so the admin can go
    // and re-point them rather than being told "no" and left guessing.
    if (inUse.length > 0) {
      const named = inUse.slice(0, 5).map((site) => site.name || site.id);
      setDeleteConfirm({
        title: "Can't Delete Template",
        message:
          `"${tpl.name}" is used by ${inUse.length} site${inUse.length === 1 ? "" : "s"}` +
          ` — ${named.join(", ")}${inUse.length > named.length ? `, and ${inUse.length - named.length} more` : ""}.` +
          " Move them to another template first; a site reads its checklist from" +
          " this one every time it is opened, and would have no sections left.",
        blocked: true,
        onConfirm: () => setDeleteConfirm(null),
      });
      return;
    }

    setDeleteConfirm({
      title: "Delete Template",
      message: "Are you sure you want to delete this template?",
      onConfirm: () => {
        setDeleteConfirm(null);
        const removed = templates[i];
        const updated = templates.filter((_, idx) => idx !== i);
        setTemplates(updated);
        if (removed) {
          cancelTemplateSave(removed.id);
          deleteTemplateRemote(removed.id);
        }
        if (activeTopTab === "template" && activeTemplateIndex === i) {
          setActiveTopTab("overview");
          setActiveTemplateIndex(null);
        } else if (activeTemplateIndex !== null && activeTemplateIndex > i) {
          setActiveTemplateIndex(activeTemplateIndex - 1);
        }
      },
    });
  };

  const handleStartEdit = (sectionId: number) => {
    if (activeTemplateIndex === null) return;
    setTemplateSnapshot(
      JSON.parse(JSON.stringify(templates[activeTemplateIndex])),
    );
    setEditingSectionId(sectionId);
    setTemplateDirty(false);
  };

  const handleCancelEdit = () => {
    if (templateSnapshot && activeTemplateIndex !== null) {
      const updated = [...templates];
      updated[activeTemplateIndex] = templateSnapshot;
      setTemplates(updated);
      // Drop any debounced edit save and persist the reverted version so the
      // server doesn't keep a half-edited template.
      cancelTemplateSave(templateSnapshot.id);
      putTemplateRemote(templateSnapshot);
    }
    setEditingSectionId(null);
    setTemplateDirty(false);
  };

  const handleSaveEdit = () => {
    if (activeTemplateIndex === null) return;
    const saved = {
      ...templates[activeTemplateIndex],
      updatedAt: new Date().toISOString(),
    };
    const updated = [...templates];
    updated[activeTemplateIndex] = saved;
    setTemplates(updated);
    setEditingSectionId(null);
    setTemplateDirty(false);
    cancelTemplateSave(saved.id);
    putTemplateRemote(saved);
  };

  const handleUpdateSectionLabel = (sectionId: number, label: string) =>
    mutateActiveTemplate((tpl) => ({
      ...tpl,
      sections: tpl.sections.map((s) =>
        s.id === sectionId ? { ...s, label } : s,
      ),
    }));

  const handleMoveSection = (sectionId: number, direction: "up" | "down") =>
    mutateActiveTemplate((tpl) => {
      const idx = tpl.sections.findIndex((s) => s.id === sectionId);
      if (direction === "up" && idx === 0) return tpl;
      if (direction === "down" && idx === tpl.sections.length - 1) return tpl;
      const sections = [...tpl.sections];
      const swap = direction === "up" ? idx - 1 : idx + 1;
      [sections[idx], sections[swap]] = [sections[swap], sections[idx]];
      return { ...tpl, sections };
    });

  const handleDeleteSection = (sectionId: number) => {
    if (editingSectionId === sectionId) setEditingSectionId(null);
    mutateActiveTemplate((tpl) => ({
      ...tpl,
      sections: tpl.sections.filter((s) => s.id !== sectionId),
    }));
  };

  const handleAddSection = (type: COPSectionType = "manual") =>
    mutateActiveTemplate((tpl) => {
      const nextId =
        tpl.sections.length > 0
          ? Math.max(...tpl.sections.map((s) => s.id)) + 1
          : 1;
      const labelByType: Record<COPSectionType, string> = {
        manual: "Section",
        bom: "BOM Section",
        photo: "Photo Section",
        reports: "RET Reports",
        alignment: "Alignment Report",
        jha: "JHA",
      };
      return {
        ...tpl,
        sections: [
          ...tpl.sections,
          { id: nextId, label: labelByType[type], questions: [], type },
        ],
      };
    });

  const handleUpdateQuestion = (
    sectionId: number,
    questionKey: string,
    updates: Partial<COPQuestion>,
  ) =>
    mutateActiveTemplate((tpl) => ({
      ...tpl,
      sections: tpl.sections.map((s) =>
        s.id !== sectionId
          ? s
          : {
              ...s,
              questions: s.questions.map((q) =>
                q.key === questionKey ? { ...q, ...updates } : q,
              ),
            },
      ),
    }));

  // Drag-to-reorder: move `fromKey` to just before/after `toKey` within the
  // same section (after = dropped on the lower half of the target row).
  const handleReorderQuestion = (
    sectionId: number,
    fromKey: string,
    toKey: string,
    after: boolean,
  ) =>
    mutateActiveTemplate((tpl) => ({
      ...tpl,
      sections: tpl.sections.map((s) => {
        if (s.id !== sectionId || fromKey === toKey) return s;
        const questions = [...s.questions];
        const fromIdx = questions.findIndex((q) => q.key === fromKey);
        if (fromIdx === -1) return s;
        const [moved] = questions.splice(fromIdx, 1);
        const toIdx = questions.findIndex((q) => q.key === toKey);
        if (toIdx === -1) return s;
        questions.splice(toIdx + (after ? 1 : 0), 0, moved);
        return { ...s, questions };
      }),
    }));

  const handleAddQuestion = (sectionId: number) =>
    mutateActiveTemplate((tpl) => ({
      ...tpl,
      sections: tpl.sections.map((s) =>
        s.id !== sectionId
          ? s
          : {
              ...s,
              questions: [
                ...s.questions,
                {
                  key: `q_${Date.now()}`,
                  label: "New Question",
                  type: "text" as COPQuestionType,
                  required: false,
                },
              ],
            },
      ),
    }));

  const handleDeleteQuestion = (sectionId: number, questionKey: string) =>
    mutateActiveTemplate((tpl) => ({
      ...tpl,
      sections: tpl.sections.map((s) =>
        s.id !== sectionId
          ? s
          : {
              ...s,
              questions: s.questions.filter((q) => q.key !== questionKey),
            },
      ),
    }));

  // ── Overview handlers ─────────────────────────────────────────────────────

  const handleStartCreate = () => {
    setIngestMode("create");
    setEditingSiteId(null);
    setIngestFiles([]);
    setActiveDocumentIndex(0);
    setExtractedSiteData({ ...BLANK_EXTRACTED });
    setIngestLocked(false);
    setIsIngesting(true);
  };

  const handleEditSite = (site: COPSite) => {
    setIngestMode("edit");
    setEditingSiteId(site.id);
    // Show loading overlay while blobs are fetched from storage. The form
    // (right panel) is usable immediately; the overlay only covers the
    // document viewer + slot strip on the left.
    const hasFilesToLoad = (site.meta.sourceFiles || []).length > 0;
    setIsLoadingSiteFiles(hasFilesToLoad);
    setIngestFiles([]);

    // Recover files — try in-memory cache first, then IndexedDB
    const storedFiles = siteFilesRef.current.get(site.id) ?? [];
    const filePromises: Promise<COPIngestFile>[] = site.meta.sourceFiles.map(
      async (f, i) => {
        const slot: COPIngestSlot = f.slot ?? "additional";
        const docKey = slot === "fixed" ? f.docKey : undefined;
        const docLabel =
          slot === "fixed"
            ? (f.docLabel ?? copDocLabelForKey(docKey))
            : undefined;
        const fileObj = storedFiles[i];
        if (fileObj) {
          const previewUrl = URL.createObjectURL(fileObj);
          objectUrlsRef.current.push(previewUrl);
          return {
            name: f.name,
            type: f.type,
            previewUrl,
            size: fileObj.size,
            fileObj,
            slot,
            docKey,
            docLabel,
            blobName: f.blobName,
          };
        }
        // Fallback: load from backend blob
        try {
          const blobName = f.blobName;
          if (blobName) {
            const res = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(site.id)}/${encodeURIComponent(f.name)}?blobName=${encodeURIComponent(blobName)}&format=base64`,
              { credentials: "include" },
            );
            if (res.ok) {
              const { base64, contentType } = await res.json();
              const binary = atob(base64);
              const bytes = new Uint8Array(binary.length);
              for (let j = 0; j < binary.length; j++)
                bytes[j] = binary.charCodeAt(j);
              const blob = new Blob([bytes], { type: contentType });
              const previewUrl = URL.createObjectURL(blob);
              objectUrlsRef.current.push(previewUrl);
              return {
                name: f.name,
                type: f.type,
                previewUrl,
                size: blob.size,
                slot,
                docKey,
                docLabel,
                blobName,
              };
            }
          }
        } catch (err) {
          console.error("Failed to load file from backend:", err);
        }
        return {
          name: f.name,
          type: f.type,
          previewUrl: "",
          size: 0,
          slot,
          docKey,
          docLabel,
          blobName: f.blobName,
        };
      },
    );
    Promise.all(filePromises)
      .then((loadedFiles) => {
        setIngestFiles(loadedFiles);
        setActiveDocumentIndex(0);
      })
      .finally(() => {
        setIsLoadingSiteFiles(false);
      });
    setExtractedSiteData({
      siteId: site.meta.siteId,
      siteName: site.meta.siteName ?? "",
      faLocationCode: site.meta.faLocationCode ?? "",
      usid: site.meta.usid ?? "",
      iwm: site.meta.iwm ?? "",
      ptn: site.meta.ptn ?? "",
      market: site.meta.market ?? "",
      financeRegion: site.meta.financeRegion ?? "",
      financeSubmarket: site.meta.financeSubmarket ?? "",
      projectName: site.meta.projectName ?? "",
      address: site.meta.address ?? "",
      city: site.meta.city ?? "",
      county: site.meta.county ?? "",
      state: site.meta.state ?? "",
      zip: site.meta.zip ?? "",
      coordinates: site.meta.coordinates ?? "",
      azimuths: normalizeAzimuths(site.meta.azimuths),
      controllingRets: normalizeControllingRets(site.meta.controllingRets),
      retPositions: normalizeRetPositions(site.meta.retPositions),
      newUseIdsBySector: site.meta.newUseIdsBySector ?? {},
      antennaPositionsBySector: site.meta.antennaPositionsBySector ?? {},
      scopeOfWork: site.meta.scopeOfWork ?? "",
    });
    setIngestLocked(
      [
        "Assign Crew",
        "Active",
        "Complete",
        "Submitted",
        "Approved",
        "Archived",
      ].includes(site.status),
    );
    setIsIngesting(true);
  };

  const handleDeleteSite = (siteId: string) => {
    setDeleteConfirm({
      title: "Delete Site",
      message:
        "Are you sure you want to delete this site? This action cannot be undone.",
      onConfirm: () => {
        setDeleteConfirm(null);
        // Single DELETE removes the site from the org's array AND purges its
        // stored files (ingest docs + wizard photos + reports) and wizard
        // state — all server-side and concurrency-safe.
        const updated = sites.filter((s) => s.id !== siteId);
        setSites(updated);
        deleteSiteRemote(siteId);
      },
    });
  };

  // ── Crew Assign ─────────────────────────────────────────────────────────
  // Record a progression step (timestamp + actor) onto a site and reflect the
  // returned history in local state (list + open view/modal).
  const recordHistory = useCallback(
    async (siteId: string, step: SiteHistoryStepKey) => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/smrtcop/history/${encodeURIComponent(siteId)}`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              step,
              byName: user?.name || user?.email || "",
            }),
          },
        );
        if (!r.ok) throw new Error(`history ${r.status}`);
        const data = await r.json();
        const history = (data.history || {}) as SiteHistory;
        setSites((prev) =>
          prev.map((s) => (s.id === siteId ? { ...s, history } : s)),
        );
        setViewingSite((prev) =>
          prev && prev.id === siteId ? { ...prev, history } : prev,
        );
        setHistorySite((prev) =>
          prev && prev.id === siteId ? { ...prev, history } : prev,
        );
      } catch (err) {
        console.error("Failed to record history:", err);
      }
    },
    [user],
  );

  // Displayed progression status. Uses recorded history when present; for
  // sites created before history tracking, infers a sensible status from the
  // legacy status enum so the list isn't all "Verify Site Details".
  const progressStatusFor = (site: COPSite): string => {
    if (site.history && Object.keys(site.history).length > 0) {
      return deriveProgressStatus(site.history);
    }
    switch (site.status) {
      case "Approved":
        return "Client Approved";
      case "Complete":
      case "Submitted":
        return "Send Tape Drop";
      case "Active":
        return "Tower In Progress";
      case "Assign Crew":
        return "Assign Crew";
      default:
        return "Verify Site Details";
    }
  };

  // Approved sites leave the Overview list and live under Approved ▸ <year>.
  // The year is the date the client approval was confirmed; sites approved
  // before the history step existed fall back to their last update.
  const isApproved = (site: COPSite) => site.status === "Approved";
  const approvalYear = (site: COPSite): string =>
    new Date(
      site.history?.clientApproved?.at || site.updatedAt,
    ).getFullYear().toString();

  const openSites = sites.filter((s) => !isApproved(s));
  const approvedSites = sites.filter(isApproved);
  const approvedYears = Array.from(
    new Set(approvedSites.map(approvalYear)),
  ).sort((a, b) => Number(b) - Number(a));
  const shownApprovedYear =
    approvedYear && approvedYears.includes(approvedYear)
      ? approvedYear
      : approvedYears[0] || null;
  const approvedSitesForYear = approvedSites.filter(
    (s) => approvalYear(s) === shownApprovedYear,
  );

  // ── Template tabs ─────────────────────────────────────────────────────────
  // Split the same way the delete affordance does: the built-in "3 Sector" is
  // the one every org has and can't remove, so it stays inline on the top row;
  // everything else was built by hand and lives on the second row behind the
  // triangle. Original indices are carried through — activeTemplateIndex and
  // handleDeleteTemplate both address `templates` by position, so filtering
  // without them would delete the wrong template.
  const isDefaultTemplate = (tpl: COPTemplate) => tpl.name === "3 Sector";
  const indexedTemplates = templates.map((tpl, index) => ({ tpl, index }));
  const defaultTemplateTabs = indexedTemplates.filter(({ tpl }) =>
    isDefaultTemplate(tpl),
  );
  const customTemplateTabs = indexedTemplates.filter(
    ({ tpl }) => !isDefaultTemplate(tpl),
  );

  // One renderer for both rows, so a tab behaves identically wherever it sits.
  const renderTemplateTab = (tpl: COPTemplate, i: number) => (
    <div
      key={tpl.id}
      className={`smrtcop-tab smrtcop-tab-template${activeTopTab === "template" && i === activeTemplateIndex ? " smrtcop-tab-active" : ""}`}
      onClick={() => {
        setActiveTopTab("template");
        setActiveTemplateIndex(i);
      }}
      onDoubleClick={() => {
        setRenamingTabId(tpl.id);
        setRenamingTabValue(tpl.name);
      }}
    >
      {tpl.name}
      {/* A template in use loses its × altogether — the strongest guard is not
          offering the action. A padlock takes its place rather than nothing at
          all, because the admin still needs to find out WHICH sites are holding
          it, and clicking the padlock is what says so. */}
      {!isDefaultTemplate(tpl) &&
        (sitesUsingTemplate(tpl.id).length > 0 ? (
          <span
            className="smrtcop-tab-locked"
            title={`In use by ${sitesUsingTemplate(tpl.id).length} site${
              sitesUsingTemplate(tpl.id).length === 1 ? "" : "s"
            } — click to see which`}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteTemplate(i);
            }}
          >
            🔒
          </span>
        ) : (
          <span
            className="smrtcop-tab-close"
            title="Delete template"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteTemplate(i);
            }}
          >
            ×
          </span>
        ))}
    </div>
  );

  // Lift the client-approval lock. Reason is mandatory — it's the audit record
  // that lands in the Log tab.
  const handleReopenApproval = async () => {
    if (!reopenApprovalSite || !reopenApprovalReason.trim()) return;
    setReopenApprovalBusy(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/smrtcop/sites/${encodeURIComponent(
          reopenApprovalSite.id,
        )}/reopen-approval`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason: reopenApprovalReason.trim() }),
        },
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.error || `Reopen failed (${r.status})`);
      }
      const data = await r.json();
      setSites((prev) =>
        prev.map((s) =>
          s.id === reopenApprovalSite.id
            ? {
                ...s,
                status: "Complete" as const,
                history: (data.history || {}) as SiteHistory,
                updatedAt: new Date().toISOString(),
              }
            : s,
        ),
      );
      setReopenApprovalSite(null);
      setReopenApprovalReason("");
      setLogRefresh((n) => n + 1);
    } catch (err) {
      setVerifyConfirm({
        title: "Reopen Failed",
        message: (err as Error).message || "Could not reopen this site.",
        onConfirm: () => setVerifyConfirm(null),
        infoOnly: true,
      });
    } finally {
      setReopenApprovalBusy(false);
    }
  };

  const handleAssignCrew = async ({
    crewLeadId,
    memberIds,
  }: {
    crewLeadId: string;
    memberIds: string[];
  }) => {
    if (!assigningSiteId) return;

    // The lead is always part of the crew; keep it first and de-dupe the rest.
    const assignedUserIds = Array.from(
      new Set([crewLeadId, ...memberIds.filter((id) => id !== crewLeadId)]),
    );
    // Stamp id→name for every assignee so the crew wizard can label per-step
    // assignments without its own user-list fetch.
    const crewNames: Record<string, string> = {};
    for (const id of assignedUserIds) {
      crewNames[id] = users.find((u) => u.id === id)?.name || id;
    }

    let changed: COPSite | undefined;
    let firstAssignment = false;
    const updated = sites.map((s) => {
      if (s.id !== assigningSiteId) return s;
      // Only the very first assignment advances the site into the wizard; later
      // edits (adding/removing crew) leave the site's progress untouched.
      firstAssignment = s.status === "Assign Crew";
      changed = {
        ...s,
        crewLeadId,
        assignedUserIds,
        crewNames,
        // Drop any step assignments pointing at crew who are no longer assigned.
        stepAssignments: Object.fromEntries(
          Object.entries(s.stepAssignments || {}).filter(([, uid]) =>
            assignedUserIds.includes(uid),
          ),
        ),
        status: firstAssignment ? ("Active" as COPSite["status"]) : s.status,
        updatedAt: new Date().toISOString(),
      };
      return changed;
    });

    setSites(updated);
    setShowAssignModal(false);
    setAssigningSiteId(null);

    if (changed) {
      // Save the assignment FIRST, then record the history step. The site PUT
      // carries the whole site (incl. its old history), so firing both at once
      // lets the PUT clobber the `crewAssigned` step the history write just
      // added — leaving the status stuck on "Assign Crew". Sequencing mirrors
      // handleSaveSite. Only record the step on the first assignment.
      await putSiteRemote(changed);
      if (firstAssignment) await recordHistory(changed.id, "crewAssigned");
    }
  };

  // Pre-fill values for the assign-crew modal from the site being edited.
  const assigningSite = sites.find((s) => s.id === assigningSiteId);
  const assignInitialLead =
    assigningSite?.crewLeadId || assigningSite?.assignedUserIds?.[0] || "";
  const assignInitialMembers = (assigningSite?.assignedUserIds || []).filter(
    (id) => id !== assignInitialLead,
  );

  // ── File handling ─────────────────────────────────────────────────────────

  // Extract structured site metadata from the RFDS document only.
  // Called exclusively from the RFDS fixed slot; other uploads must not invoke this.
  const extractFromRfds = (rfdsFile: File) => {
    if (
      rfdsFile.type !== "application/pdf" &&
      !rfdsFile.type.startsWith("image/")
    )
      return;
    if (!extractedSiteData) return;
    const formData = new FormData();
    formData.append("file", rfdsFile);
    setRfdsExtracting(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/smrt-cop/extract`, {
      method: "POST",
      credentials: "include",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data || data.error) return;

        // Azimuths: backend returns structured { A, B, C } — store directly
        // (each sector renders in its own input box).
        const bySector = (data.azimuthsBySector || {}) as Partial<COPAzimuths>;
        const nextAz: COPAzimuths = {
          A: (bySector.A ?? "").toString().trim(),
          B: (bySector.B ?? "").toString().trim(),
          C: (bySector.C ?? "").toString().trim(),
        };

        // Controlling RET: backend returns { A: {p1,p4}, B, C } from the RFDS
        // "Cell Details Final". Merge non-empty extracted values over whatever
        // the verifier has already entered (and keep Delta if present).
        const crIn = (data.controllingRetsBySector || {}) as Record<
          string,
          { p1?: string; p4?: string } | undefined
        >;
        const mergeCR = (
          prevCR: COPControllingRet,
          inc?: { p1?: string; p4?: string },
        ): COPControllingRet => ({
          p1: (inc?.p1 ?? "").toString().trim() || prevCR.p1,
          p4: (inc?.p4 ?? "").toString().trim() || prevCR.p4,
        });

        setExtractedSiteData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            siteId: data.siteId || prev.siteId,
            siteName: data.siteName || prev.siteName,
            faLocationCode: data.faLocationCode || prev.faLocationCode,
            usid: data.usid || prev.usid,
            market: data.market || prev.market,
            financeRegion: data.financeRegion || prev.financeRegion,
            financeSubmarket: data.financeSubmarket || prev.financeSubmarket,
            projectName: data.projectName || prev.projectName,
            address: data.physicalAddress || prev.address,
            city: data.city || prev.city,
            county: data.county || prev.county,
            state: data.state || prev.state,
            zip: data.zip || prev.zip,
            coordinates: data.coordinates || prev.coordinates,
            azimuths: {
              // Spread first so a verifier-added Delta survives a re-extract.
              ...prev.azimuths,
              A: nextAz.A || prev.azimuths.A,
              B: nextAz.B || prev.azimuths.B,
              C: nextAz.C || prev.azimuths.C,
            },
            controllingRets: {
              ...prev.controllingRets,
              A: mergeCR(prev.controllingRets.A, crIn.A),
              B: mergeCR(prev.controllingRets.B, crIn.B),
              C: mergeCR(prev.controllingRets.C, crIn.C),
              ...(prev.controllingRets.D !== undefined
                ? { D: mergeCR(prev.controllingRets.D, crIn.D) }
                : {}),
            },
            // One position is legitimate now (single-antenna-change sites), so
            // accept any non-empty list rather than requiring a pair.
            retPositions:
              Array.isArray(data.retPositions) && data.retPositions.length >= 1
                ? normalizeRetPositions(data.retPositions)
                : prev.retPositions,
            // USEIDs (NEW) from the RFDS CellDetails(Final), grouped by sector.
            newUseIdsBySector:
              data.newUseIdsBySector &&
              Object.keys(data.newUseIdsBySector).length > 0
                ? data.newUseIdsBySector
                : prev.newUseIdsBySector,
            // Antenna model + linked cells per sector — keep the previous read
            // if this one came back empty, matching the USEID rule above so a
            // partial re-extract can't blank a good ingestion.
            antennaPositionsBySector:
              data.antennaPositionsBySector &&
              Object.keys(data.antennaPositionsBySector).length > 0
                ? data.antennaPositionsBySector
                : prev.antennaPositionsBySector,
          };
        });
      })
      .catch((err) => console.error("OCR extraction failed:", err))
      .finally(() => setRfdsExtracting(false));
  };

  // Extract the "Scope of Work Summary" cell from the DE 130 Excel workbook
  // and populate the Scope of Work field. Scoped to the de_130 fixed slot only.
  const extractFromDe130 = (de130File: File) => {
    if (!extractedSiteData) return;
    const formData = new FormData();
    formData.append("file", de130File);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/smrt-cop/extract-de130`, {
      method: "POST",
      credentials: "include",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data || data.error) return;
        const sow = (data.scopeOfWork || "").toString().trim();
        if (!sow) return;
        setExtractedSiteData((prev) =>
          prev ? { ...prev, scopeOfWork: sow } : prev,
        );
      })
      .catch((err) => console.error("DE 130 extraction failed:", err));
  };

  // Extract the IWM job number + PTN from the Construction Drawings page-1
  // header line "IWM/ PTN: <string> / <string>" — IWM is the FIRST token, PTN
  // the SECOND (after the "/"). Scoped to the construction_drawings slot only.
  const extractFromConstruction = (cdFile: File) => {
    if (
      cdFile.type !== "application/pdf" &&
      !cdFile.type.startsWith("image/")
    )
      return; // DWG/CAD can't be parsed — leave IWM/PTN for manual entry
    if (!extractedSiteData) return;
    const formData = new FormData();
    formData.append("file", cdFile);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/smrt-cop/extract-construction`, {
      method: "POST",
      credentials: "include",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data || data.error) return;
        const iwm = (data.iwm || "").toString().trim();
        const ptn = (data.ptn || "").toString().trim();
        if (!iwm && !ptn) return;
        // Don't clobber a value the verifier already typed.
        setExtractedSiteData((prev) =>
          prev
            ? { ...prev, iwm: prev.iwm || iwm, ptn: prev.ptn || ptn }
            : prev,
        );
      })
      .catch((err) =>
        console.error("Construction IWM/PTN extraction failed:", err),
      );
  };

  // Upload into a fixed slot — takes only the first selected file, replaces any existing one
  const handleFixedSlotUpload = (
    key: CopDocKey,
    label: string,
    fileList: FileList | null,
  ) => {
    if (!fileList || fileList.length === 0) return;
    const file = fileList[0];
    const previewUrl = URL.createObjectURL(file);
    objectUrlsRef.current.push(previewUrl);

    setIngestFiles((prev) => {
      // Revoke previous preview URL for this slot
      const existing = prev.find((f) => f.slot === "fixed" && f.docKey === key);
      if (existing?.previewUrl) {
        URL.revokeObjectURL(existing.previewUrl);
        objectUrlsRef.current = objectUrlsRef.current.filter(
          (u) => u !== existing.previewUrl,
        );
      }
      const withoutOld = prev.filter(
        (f) => !(f.slot === "fixed" && f.docKey === key),
      );
      const newEntry: COPIngestFile = {
        name: file.name,
        previewUrl,
        type: file.type,
        size: file.size,
        fileObj: file,
        slot: "fixed",
        docKey: key,
        docLabel: label,
      };
      const next = [...withoutOld, newEntry];
      setActiveDocumentIndex(next.length - 1);
      return next;
    });

    // Per-slot auto-fill hooks — each extractor is scoped to its own slot.
    if (key === "rfds") extractFromRfds(file);
    if (key === "de_130") extractFromDe130(file);
    if (key === "construction_drawings") extractFromConstruction(file);
  };

  // Append files to the Additional Documents section (multi-file allowed).
  // These are never used as an OCR source — RFDS is the sole source.
  const handleAddAdditionalFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    const newEntries: COPIngestFile[] = files.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      objectUrlsRef.current.push(previewUrl);
      return {
        name: file.name,
        previewUrl,
        type: file.type,
        size: file.size,
        fileObj: file,
        slot: "additional",
      };
    });
    setIngestFiles((prev) => {
      const next = [...prev, ...newEntries];
      setActiveDocumentIndex(next.length - 1);
      return next;
    });
  };

  const removeIngestFile = (index: number) => {
    const removed = ingestFiles[index];
    if (removed?.previewUrl) {
      URL.revokeObjectURL(removed.previewUrl);
      objectUrlsRef.current = objectUrlsRef.current.filter(
        (u) => u !== removed.previewUrl,
      );
    }
    setIngestFiles((prev) => prev.filter((_, i) => i !== index));
    setActiveDocumentIndex((prev) =>
      prev >= index && prev > 0 ? prev - 1 : prev,
    );
  };

  const handleCancelIngest = () => {
    revokeAllUrls();
    setIsIngesting(false);
    setIngestMode("create");
    setEditingSiteId(null);
    setIngestFiles([]);
    setActiveDocumentIndex(0);
    setExtractedSiteData(null);
    setIngestLocked(false);
    setIsLoadingSiteFiles(false);
  };

  // ── Form handlers ─────────────────────────────────────────────────────────

  const updateExtracted = (
    field: keyof COPExtractedSiteData,
    value: string,
  ) => {
    setExtractedSiteData((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const updateAzimuth = (sector: keyof COPAzimuths, value: string) => {
    setExtractedSiteData((prev) =>
      prev
        ? { ...prev, azimuths: { ...prev.azimuths, [sector]: value } }
        : prev,
    );
  };

  const updateControllingRet = (
    sector: keyof COPControllingRets,
    port: keyof COPControllingRet,
    value: string,
  ) => {
    setExtractedSiteData((prev) =>
      prev
        ? {
            ...prev,
            controllingRets: {
              ...prev.controllingRets,
              [sector]: {
                ...(prev.controllingRets[sector] ?? { p1: "", p4: "" }),
                [port]: value,
              },
            },
          }
        : prev,
    );
  };

  // Edit one of the site's RET antenna positions (index 0 → the "p1" slot,
  // index 1 → the "p4" slot). Kept as-typed (not re-sorted) so slot↔position
  // stays stable while the admin corrects a value.
  const updateRetPosition = (index: 0 | 1, value: string) => {
    const n = parseInt(value, 10);
    setExtractedSiteData((prev) => {
      if (!prev) return prev;
      const next = [...prev.retPositions];
      next[index] = Number.isFinite(n) ? n : prev.retPositions[index];
      return { ...prev, retPositions: next };
    });
  };

  // Add / drop the SECOND RET position. Sites where the RFDS shows only one
  // antenna changing need a single RET report, so the second position (and
  // every "p4" slot that hangs off it) must be removable — and re-addable when
  // the RFDS read was wrong.
  const addSecondRetPosition = () =>
    setExtractedSiteData((prev) =>
      prev && prev.retPositions.length < 2
        ? { ...prev, retPositions: [...prev.retPositions, (prev.retPositions[0] ?? 0) + 1] }
        : prev,
    );
  const removeSecondRetPosition = () =>
    setExtractedSiteData((prev) =>
      prev && prev.retPositions.length > 1
        ? { ...prev, retPositions: prev.retPositions.slice(0, 1) }
        : prev,
    );

  // Reveal the optional Delta (fourth-sector) fields — azimuth AND controlling
  // RET together, so the two stay in step. Stored empty until the verifier
  // fills them.
  const addDeltaAzimuth = () =>
    setExtractedSiteData((prev) =>
      prev
        ? {
            ...prev,
            azimuths: { ...prev.azimuths, D: prev.azimuths.D ?? "" },
            controllingRets: {
              ...prev.controllingRets,
              D: prev.controllingRets.D ?? { p1: "", p4: "" },
            },
          }
        : prev,
    );

  // Remove the Delta azimuth + controlling RET fields entirely.
  const removeDeltaAzimuth = () =>
    setExtractedSiteData((prev) => {
      if (!prev) return prev;
      const { D: _azD, ...azRest } = prev.azimuths;
      const { D: _crD, ...crRest } = prev.controllingRets;
      return { ...prev, azimuths: azRest, controllingRets: crRest };
    });

  const handleSaveSite = async (overrideStatus?: COPSite["status"]) => {
    if (!extractedSiteData) return;

    // Hold off the background poll for the whole save (uploads + PUT) so it
    // can't briefly revert the optimistic list update mid-flight.
    writesInFlightRef.current++;

    let updated: COPSite[];
    let finalId: string;

    if (ingestMode === "edit" && editingSiteId) {
      finalId = editingSiteId;
      updated = sites.map((s) => {
        if (s.id !== finalId) return s;
        return {
          ...s,
          ...(overrideStatus ? { status: overrideStatus } : {}),
          name: extractedSiteData.siteId,
          updatedAt: new Date().toISOString(),
          meta: {
            siteId: extractedSiteData.siteId,
            siteName: extractedSiteData.siteName,
            faLocationCode: extractedSiteData.faLocationCode,
            usid: extractedSiteData.usid,
            iwm: extractedSiteData.iwm,
            ptn: extractedSiteData.ptn,
            market: extractedSiteData.market,
            financeRegion: extractedSiteData.financeRegion,
            financeSubmarket: extractedSiteData.financeSubmarket,
            projectName: extractedSiteData.projectName,
            address: extractedSiteData.address,
            city: extractedSiteData.city,
            county: extractedSiteData.county,
            state: extractedSiteData.state,
            zip: extractedSiteData.zip,
            coordinates: extractedSiteData.coordinates,
            azimuths: extractedSiteData.azimuths,
            controllingRets: extractedSiteData.controllingRets,
            retPositions: extractedSiteData.retPositions,
            newUseIdsBySector: extractedSiteData.newUseIdsBySector,
            antennaPositionsBySector:
              extractedSiteData.antennaPositionsBySector,
            scopeOfWork: extractedSiteData.scopeOfWork,
            sourceFiles: ingestFiles.map((f) => ({
              name: f.name,
              type: f.type,
              slot: f.slot,
              docKey: f.docKey,
              docLabel: f.docLabel,
              blobName: f.blobName,
            })),
          },
        };
      });
    } else {
      finalId = `site_${Date.now()}`;
      const newSite = buildCOPSite(extractedSiteData, ingestFiles, finalId);
      updated = [
        ...sites,
        overrideStatus ? { ...newSite, status: overrideStatus } : newSite,
      ];
    }

    // Cache File objects so this site's files can be previewed if edited again this session
    const fileObjects = ingestFiles
      .filter((f) => f.fileObj)
      .map((f) => f.fileObj!);
    if (fileObjects.length > 0) siteFilesRef.current.set(finalId, fileObjects);

    // Upload files to Azure Blob — only those that haven't been uploaded yet (fileObj present)
    for (let i = 0; i < ingestFiles.length; i++) {
      const f = ingestFiles[i];
      if (!f.fileObj) continue;
      const fieldKey =
        f.slot === "fixed" && f.docKey ? f.docKey : COP_ADDITIONAL_FIELD_KEY;
      const formData = new FormData();
      formData.append("file", f.fileObj);
      try {
        const uploadRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(finalId)}/${encodeURIComponent(fieldKey)}`,
          { method: "POST", credentials: "include", body: formData },
        );
        if (uploadRes.ok) {
          const ref = await uploadRes.json();
          // Backfill blobName onto the matching sourceFiles entry by slot + docKey + filename + index
          const siteIdx = updated.findIndex((s: COPSite) => s.id === finalId);
          if (siteIdx !== -1) {
            const sf = updated[siteIdx].meta.sourceFiles[i];
            if (sf) sf.blobName = ref.blobName;
          }
        }
      } catch (err) {
        console.error("Failed to upload file:", err);
      }
    }

    setSites(updated);
    // Persist just this site (concurrency-safe upsert) — never the whole list.
    const savedSite = updated.find((s) => s.id === finalId);
    if (savedSite) await putSiteRemote(savedSite);
    // Record progression: a brand-new site = "Site Added"; an admin verifying
    // the ingested details (status → Assign Crew) = "Site Details Verified".
    // Awaited in sequence so the two history writes don't race each other.
    const isNew = !(ingestMode === "edit" && editingSiteId);
    if (isNew) await recordHistory(finalId, "siteAdded");
    if (overrideStatus === "Assign Crew")
      await recordHistory(finalId, "detailsVerified");
    writesInFlightRef.current--;
    handleCancelIngest();
  };

  const handleAdminReview = () => {
    if (ingestLocked) {
      // Complete sites cannot be unlocked — must use Reopen Site
      const editSite = sites.find((s) => s.id === editingSiteId);
      // Client-approved sites are frozen server-side; the only way in is a
      // logged Reopen Approval from the Complete tab.
      if (editSite?.status === "Approved") {
        setVerifyConfirm({
          title: "Site Locked",
          message:
            "This site has been Client Approved and is locked. Use 'Reopen Approval' on the Complete tab to make further changes.",
          onConfirm: () => setVerifyConfirm(null),
          infoOnly: true,
        });
        return;
      }
      if (editSite?.status === "Complete") {
        setVerifyConfirm({
          title: "Site Locked",
          message:
            "This site has already been submitted. To make changes, please use the 'Reopen Site' option.",
          onConfirm: () => setVerifyConfirm(null),
          infoOnly: true,
        });
        return;
      }
      setVerifyConfirm({
        title: "Edit Site Information",
        message: "Are you sure you want to edit this site information?",
        onConfirm: () => {
          setVerifyConfirm(null);
          setIngestLocked(false);
        },
      });
      return;
    }
    if (!canVerify) return;
    setVerifyConfirm({
      title: "Verify Site Information",
      message:
        "Please confirm you have reviewed the site info and attest to its accuracy.",
      onConfirm: async () => {
        if (isVerifying) return;
        setVerifyConfirm(null);
        setIsVerifying(true);
        try {
          await handleSaveSite("Assign Crew");
        } finally {
          setIsVerifying(false);
        }
      },
    });
  };

  // ── Document preview ──────────────────────────────────────────────────────

  const activeFile = ingestFiles[activeDocumentIndex] ?? null;

  // Excel files are not previewed in-app — clicking one triggers a download
  // so the user can open it in their native spreadsheet app.
  const isExcelFile = (f: COPIngestFile | null | undefined): boolean => {
    if (!f) return false;
    if (/\.xlsx?$/i.test(f.name)) return true;
    return f.type.includes("spreadsheet") || f.type.includes("ms-excel");
  };

  // Href for saving a file to open outside the in-app preview (Excel → native
  // spreadsheet app). Stored files get a navigational backend attachment URL —
  // a real anchor click the browser owns, the one download pattern an RBI /
  // managed browser permits (synthetic clicks on script-created anchors are
  // suppressed). Files picked this session but not yet uploaded exist only
  // locally, so their object URL + download attribute is the only option.
  // Returns null when the file has neither (edit-mode entry whose bytes
  // couldn't be recovered) — no download is possible.
  const externalFileHref = (file: COPIngestFile): string | null => {
    if (file.blobName && editingSiteId) {
      return `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(editingSiteId)}/${encodeURIComponent(file.name)}?blobName=${encodeURIComponent(file.blobName)}&format=inline&disposition=attachment`;
    }
    return file.previewUrl || null;
  };

  // The download attribute is only for local object URLs (same-origin); on the
  // cross-origin backend URL it is ignored and the server names the file.
  const externalFileDownloadName = (file: COPIngestFile): string | undefined =>
    file.blobName && editingSiteId ? undefined : file.name;

  const renderDocumentPreview = (file: COPIngestFile | null) => {
    if (!file) {
      return (
        <div className="smrtcop-viewer-empty">
          No file selected
          <span style={{ fontSize: "12px", color: "#555" }}>
            Use "+ Add File" to upload
          </span>
        </div>
      );
    }
    if (!file.previewUrl) {
      return (
        <div className="smrtcop-viewer-empty">
          <span>{file.name}</span>
          <span style={{ fontSize: "12px", color: "#555" }}>
            Re-upload this file to preview it
          </span>
        </div>
      );
    }
    if (file.type === "application/pdf") {
      return <iframe src={file.previewUrl} title={file.name} />;
    }
    if (file.type.startsWith("image/")) {
      return <img src={file.previewUrl} alt={file.name} />;
    }
    if (isExcelFile(file)) {
      const href = externalFileHref(file);
      return (
        <div className="smrtcop-viewer-empty">
          <span>{file.name}</span>
          {href && (
            <a
              className="smrtcop-btn-action smrtcop-btn-save"
              style={{
                flex: "none",
                width: "auto",
                padding: "0 14px",
                height: 30,
                fontSize: "0.82rem",
                display: "inline-flex",
                alignItems: "center",
                textDecoration: "none",
              }}
              href={href}
              download={externalFileDownloadName(file)}
            >
              Open in spreadsheet app
            </a>
          )}
          <span style={{ fontSize: "11px", color: "#555" }}>
            Excel previews are not shown in-app — click to download and open
            locally.
          </span>
        </div>
      );
    }
    return (
      <div className="smrtcop-viewer-empty">
        <span>{file.name}</span>
        <span style={{ fontSize: "12px", color: "#555" }}>
          Preview not supported for this file type
        </span>
      </div>
    );
  };

  const canConfirm =
    !!extractedSiteData && extractedSiteData.siteId.trim().length > 0;

  const missingRequiredDocs = COP_FIXED_DOCS.filter((d) => d.required)
    .filter(
      (d) => !ingestFiles.some((f) => f.slot === "fixed" && f.docKey === d.key),
    )
    .map((d) => d.label);

  const canVerify = canConfirm && missingRequiredDocs.length === 0;

  const activeTemplate =
    activeTemplateIndex !== null ? templates[activeTemplateIndex] : null;

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="smrtcop-page">
      {/* ══════════════ ADMIN VIEW (submitted summary for a site) ══════════════ */}
      {!isIngesting &&
        viewingTemplate &&
        viewingSite &&
        (() => {
          // Before final submission, surface each COMPLETED wizard step's live
          // answers so the admin view populates as the crew presses "Continue".
          // After submit, use the frozen `submitted` snapshot. `__completedSteps`
          // holds the template section ids the crew has continued past.
          const answersData: Record<string, any> = viewingSiteState.answers || {};
          const rawSubmitted: Record<string, any> = viewingSiteState.submitted || {};
          const hasSubmission = Object.keys(rawSubmitted).length > 0;
          const submittedData: Record<string, any> = hasSubmission
            ? rawSubmitted
            : answersData;
          const completedSteps: number[] = Array.isArray(answersData.__completedSteps)
            ? answersData.__completedSteps
            : [];
          const isStepRevealed = (sectionId: number) =>
            hasSubmission || completedSteps.includes(sectionId);
          const viewSections = viewingTemplate.sections.filter(
            (s) => !s.label.toLowerCase().includes("declaration"),
          );
          const labelSt: React.CSSProperties = {
            fontSize: "0.75rem",
            color: "#9aa0a6",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            marginBottom: 4,
          };

          // Find certification meta + history from any certification question
          let certMeta: any = null;
          let certHistory: any[] = [];
          for (const sec of viewingTemplate.sections) {
            for (const q of sec.questions) {
              if (q.type === "certification") {
                if (submittedData[`${q.key}_meta`])
                  certMeta = submittedData[`${q.key}_meta`];
                if (submittedData[`${q.key}_history`])
                  certHistory = submittedData[`${q.key}_history`];
                break;
              }
            }
            if (certMeta || certHistory.length) break;
          }

          // Open a stored blob (photo or report) for review via the backend
          // base64 proxy (?format=base64). Direct browser SAS is disabled by
          // the storage firewall, so the backend fetches the bytes and we
          // build a local blob URL. The tab is opened synchronously so the
          // popup blocker keeps the click's user-gesture, then navigated once
          // the bytes come back. (NB: this works wherever the backend can
          // reach the storage account — i.e. prod / allowlisted hosts; a
          // local-dev backend behind the firewall will get a 403.)
          const openFile = async (fieldKey: string, blobName: string) => {
            const win = window.open("", "_blank");
            try {
              const r = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/smrtcop/files/${encodeURIComponent(viewingSite.id)}/${encodeURIComponent(fieldKey)}?blobName=${encodeURIComponent(blobName)}&format=base64`,
                { credentials: "include" },
              );
              if (!r.ok) throw new Error(`download ${r.status}`);
              const data = await r.json();
              const binary = atob(data.base64);
              const bytes = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i++)
                bytes[i] = binary.charCodeAt(i);
              const blob = new Blob([bytes], {
                type: data.contentType || "application/octet-stream",
              });
              const url = URL.createObjectURL(blob);
              if (win) win.location.href = url;
              else window.open(url, "_blank");
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            } catch (err) {
              console.error("Failed to open file:", err);
              if (win) win.close();
            }
          };

          // Photos are persisted under keys generated at wizard RUNTIME from the
          // site's checklist (e.g. `5_photo_A_0_1`, `5_photo_A_1__az_1`,
          // `5_photo_SPD_g0`, and crew-added `…__extra__<id>`), none of which
          // exist on the static template the admin holds. So we discover photos
          // directly from the submitted data: a key holds a photo when its
          // value is a filename string and it has a sibling blob/dataUrl
          // pointer. (Report slots store an object; text answers have no blob
          // sibling — both are naturally excluded.)
          const renderedPhotoKeys = new Set<string>();
          const isPhotoEntry = (k: string): boolean =>
            typeof submittedData[k] === "string" &&
            !!submittedData[k] &&
            (submittedData[`${k}_blob`] !== undefined ||
              submittedData[`${k}_dataUrl`] !== undefined);

          // Navigational exports: each Generate control is a real <a href> to
          // the backend, so the browser owns both the click and the transfer.
          // An RBI / managed browser suppresses synthetic clicks on
          // script-created anchors — a genuine click on a real link is the one
          // pattern that survives. There is no completion/error signal to
          // await; onClick only drives the short "generating…" UX (the
          // navigation proceeds regardless) and guards a double-submit via
          // pointer-events while generating.
          const exportAnchor = (
            href: string,
            status: "idle" | "generating" | "error",
            setStatus: (s: "idle" | "generating" | "error") => void,
            genTitle: string,
          ) => (
            <a
              className="smrtcop-btn-icon smrtcop-btn-view"
              aria-disabled={status === "generating"}
              style={{
                opacity: status === "generating" ? 0.6 : 1,
                pointerEvents: status === "generating" ? "none" : undefined,
              }}
              href={href}
              onClick={() => {
                setStatus("generating");
                setTimeout(() => setStatus("idle"), EXPORT_REENABLE_DELAY_MS);
              }}
              title={status === "error" ? "Generate failed — retry" : genTitle}
            >
              {status === "generating" ? (
                <span className="smrtcop-spinner" />
              ) : (
                <DownloadIcon />
              )}
            </a>
          );

          // The Ericsson BOM output — the ingested Final BOM with an "Actual
          // Installed" column added, generated server-side.
          const bomOutputUrl = `${process.env.REACT_APP_BACKEND_URL}/smrtcop/bom-output/${encodeURIComponent(viewingSite.id)}`;

          // The populated RG114 Tape Drop Form (.xlsm), generated server-side
          // from verified site data + wizard answers.
          const tapeDropOutputUrl = `${process.env.REACT_APP_BACKEND_URL}/smrtcop/tape-drop/${encodeURIComponent(viewingSite.id)}`;

          // The populated AT&T "RET Information" worksheet (.xlsx), built
          // server-side from the uploaded .txtrpt scans + site data. The
          // Installer ID = assigned crew lead's name (passed so the backend
          // needn't re-resolve it via Auth0).
          const retOutputUrl = (() => {
            const leadId =
              viewingSite.crewLeadId || (viewingSite.assignedUserIds || [])[0];
            const lead = users.find((u) => u.id === leadId);
            const q = lead?.name
              ? `?installer=${encodeURIComponent(lead.name)}`
              : "";
            return `${process.env.REACT_APP_BACKEND_URL}/smrtcop/ret-form/${encodeURIComponent(viewingSite.id)}${q}`;
          })();

          const rg123OutputUrl = `${process.env.REACT_APP_BACKEND_URL}/smrtcop/rg123/${encodeURIComponent(viewingSite.id)}`;

          // Submitter = the admin generating (email + name → initials).
          const an204OutputUrl = (() => {
            const qs = new URLSearchParams();
            if (user?.email) qs.set("email", user.email);
            if (user?.name) qs.set("name", user.name);
            const q = qs.toString() ? `?${qs.toString()}` : "";
            return `${process.env.REACT_APP_BACKEND_URL}/smrtcop/an204/${encodeURIComponent(viewingSite.id)}${q}`;
          })();

          // Record (or clear) that photos / the BOM / the tape drop have been
          // uploaded to the Ericsson portal. Optimistic, reverts on failure.
          const portalUploads = viewingSiteState.portalUploads || {};
          const confirmPortal = async (
            kind: "photos" | "bom" | "tapeDrop" | "ret" | "alignment" | "rg123",
            confirmed: boolean,
          ) => {
            const prev = viewingSiteState.portalUploads;
            setViewingSiteState((s) => ({
              ...s,
              portalUploads: {
                ...s.portalUploads,
                [kind]: confirmed ? { at: new Date().toISOString() } : null,
              },
            }));
            try {
              const r = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/smrtcop/portal-status/${encodeURIComponent(viewingSite.id)}`,
                {
                  method: "POST",
                  credentials: "include",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ kind, confirmed }),
                },
              );
              // 423 = the site is client-approved and frozen. Say so rather
              // than silently rolling the toggle back, which reads as a bug.
              if (r.status === 423) {
                const body = await r.json().catch(() => ({}));
                setViewingSiteState((st) => ({ ...st, portalUploads: prev }));
                setVerifyConfirm({
                  title: "Site Locked",
                  message:
                    body?.error ||
                    "This site has been Client Approved and is locked.",
                  onConfirm: () => setVerifyConfirm(null),
                  infoOnly: true,
                });
                return;
              }
              if (!r.ok) throw new Error(`portal-status ${r.status}`);
              const data = await r.json();
              setViewingSiteState((s) => ({
                ...s,
                portalUploads: data.portalUploads || {},
              }));
              // Mirror into the site progression history so the Status History
              // + derived status advance.
              recordHistory(
                viewingSite.id,
                kind === "photos"
                  ? "photosSent"
                  : kind === "bom"
                    ? "bomSent"
                    : kind === "ret"
                      ? "retSent"
                      : kind === "alignment"
                        ? "alignmentSent"
                        : kind === "rg123"
                          ? "rg123Sent"
                          : "tapeDropSent",
              );
            } catch (err) {
              console.error("Failed to update portal status:", err);
              setViewingSiteState((s) => ({ ...s, portalUploads: prev }));
            }
          };

          const renderPortalConfirm = (
            kind: "photos" | "bom" | "tapeDrop" | "ret" | "alignment" | "rg123",
            label: string,
          ) => {
            const entry = portalUploads[kind];
            const confirmed = !!entry;
            // Grey until actually marked sent, then green. Photos are uploaded to
            // the portal and marked "Sent" by the CREW, not the admin — so the
            // admin's photos button is READ-ONLY: it stays grey until the crew
            // confirms, then reflects green (never admin-clickable). Every other
            // artifact is admin-sent, so its button is grey until the admin
            // clicks it.
            const readOnly = kind === "photos";
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 16,
                }}
              >
                <button
                  type="button"
                  className="smrtcop-btn-action"
                  disabled={readOnly}
                  onClick={
                    readOnly
                      ? undefined
                      : () => confirmPortal(kind, !confirmed)
                  }
                  title={
                    readOnly
                      ? confirmed
                        ? "Photos marked sent by the crew"
                        : "Photos are sent by the crew — waiting for their confirmation"
                      : undefined
                  }
                  style={{
                    flex: "none",
                    width: 180,
                    height: 34,
                    fontSize: "0.85rem",
                    background: confirmed ? "#1faa59" : "#2a2a2a",
                    borderColor: confirmed ? "#1faa59" : "#2a2a2a",
                    color: "#fff",
                    // Keep full colour even when the photos button is disabled
                    // (it's read-only, not "inactive"), so grey/green read clearly.
                    opacity: 1,
                  }}
                >
                  {confirmed ? `✓ ${label}` : label}
                </button>
                {confirmed && entry.at && (
                  <div style={{ fontSize: "0.7rem", color: "#1faa59" }}>
                    Confirmed {new Date(entry.at).toLocaleString()}
                  </div>
                )}
              </div>
            );
          };

          // Collapsed section view — just the "below the line" footer (generate
          // ⬇ + green Sent), so a collapsed card shows Header + actions only.
          // The expanded branches are left untouched.
          const collapsedFooter = (
            label: string,
            status: "idle" | "generating" | "error",
            setStatus: (s: "idle" | "generating" | "error") => void,
            href: string,
            genTitle: string,
            confirmKind:
              | "photos"
              | "bom"
              | "tapeDrop"
              | "ret"
              | "alignment"
              | "rg123",
            confirmLabel: string,
          ) => (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  borderTop: "1px solid #2a2a2a",
                  paddingTop: 12,
                  marginTop: 8,
                }}
              >
                <span style={{ fontSize: "0.85rem", color: "#c9d1d9" }}>
                  {label}
                </span>
                {exportAnchor(href, status, setStatus, genTitle)}
              </div>
              {renderPortalConfirm(confirmKind, confirmLabel)}
            </>
          );

          const renderCollapsedFooter = (
            section: COPSection,
          ): React.ReactNode => {
            // Photo sections are exempt from the reveal gate (crew photos
            // autosave live) — so their collapsed footer shows too.
            if (!isStepRevealed(section.id) && section.type !== "photo") {
              return (
                <div
                  style={{
                    textAlign: "center",
                    color: "#9aa0a6",
                    fontSize: "0.85rem",
                    fontStyle: "italic",
                    padding: "8px 0",
                  }}
                >
                  Awaiting crew — not yet completed
                </div>
              );
            }
            const label = (section.label || "").trim().toUpperCase();
            if (section.type === "reports")
              return collapsedFooter(
                "AT&T RET Worksheet",
                retGenStatus,
                setRetGenStatus,
                retOutputUrl,
                "Generate RET",
                "ret",
                "RET Sent",
              );
            if (section.type === "alignment")
              return collapsedFooter(
                "AN204 Form",
                an204GenStatus,
                setAn204GenStatus,
                an204OutputUrl,
                "Generate AN204",
                "alignment",
                "AN204 Sent",
              );
            if (section.type === "jha")
              return collapsedFooter(
                "RG123 Form",
                rg123GenStatus,
                setRg123GenStatus,
                rg123OutputUrl,
                "Generate RG123",
                "rg123",
                "RG123 Sent",
              );
            if (section.type === "bom")
              return collapsedFooter(
                "Final BOM",
                bomGenStatus,
                setBomGenStatus,
                bomOutputUrl,
                "Generate BOM",
                "bom",
                "BOM Sent",
              );
            if (section.type === "photo")
              return (
                <>
                  {renderPhotoBulkRow(section)}
                  {renderPortalConfirm("photos", "Photo's Sent")}
                </>
              );
            if (label === "TAPE DROP")
              return collapsedFooter(
                "RG114 Tape Drop Form",
                tapeGenStatus,
                setTapeGenStatus,
                tapeDropOutputUrl,
                "Generate Tape Drop",
                "tapeDrop",
                "Tape Drop Sent",
              );
            return null;
          };

          // The Ericsson portal blocks clipboard paste, so an admin uploading on
          // a crew's behalf DOWNLOADS each photo into a chosen folder (File
          // System Access API) rather than copying it. Mirrors the crew view.
          const persistDownloaded = (keys: string[]) => {
            if (!keys.length) return;
            fetch(
              `${process.env.REACT_APP_BACKEND_URL}/smrtcop/downloaded-photos/${encodeURIComponent(viewingSite.id)}`,
              {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ keys, scope: "admin" }),
              },
            ).catch((err) =>
              console.error("Failed to persist downloaded photos:", err),
            );
          };

          const ensureDirForSite = async (): Promise<any | null> => {
            if (!isFsAccessSupported()) {
              setVerifyConfirm({
                title: "Unsupported Browser",
                message:
                  "Saving photos to a folder needs Google Chrome or Microsoft Edge on desktop.",
                onConfirm: () => setVerifyConfirm(null),
                infoOnly: true,
              });
              return null;
            }
            const cur = dirHandleRef.current;
            if (cur && cur.siteId === viewingSite.id) {
              if (await ensureDirPermission(cur.handle)) return cur.handle;
              // permission revoked/lost → fall through and re-pick.
            }
            try {
              const handle = await pickPhotoDirectory();
              dirHandleRef.current = { siteId: viewingSite.id, handle };
              return handle;
            } catch {
              return null; // user cancelled the folder picker
            }
          };

          const downloadPhoto = async (
            k: string,
            blobName: string,
            fileName: string,
          ) => {
            const dir = await ensureDirForSite();
            if (!dir) return;
            setDownloadStatus((s) => ({ ...s, [k]: "saving" }));
            try {
              const bytes = await fetchPhotoBytes(
                process.env.REACT_APP_BACKEND_URL || "",
                viewingSite.id,
                k,
                blobName,
              );
              await writeFileToDir(dir, downloadFileName(k, fileName), bytes);
              setSiteDownloaded((prev) => new Set(prev).add(k));
              setDownloadStatus((s) => {
                const next = { ...s };
                delete next[k];
                return next;
              });
              persistDownloaded([k]);
            } catch (err) {
              console.error("Failed to download photo:", err);
              setDownloadStatus((s) => ({ ...s, [k]: "error" }));
            }
          };

          // Download the given photo keys (the CHECKLIST PHOTO section only —
          // NOT RET/RRH), one after another. Per-photo status tracked so a later
          // "Download Remaining" reuses this with a pre-filtered key list.
          const downloadAllPhotos = async (candidateKeys: string[]) => {
            const keys = candidateKeys.filter(
              (k) => !!submittedData[`${k}_blob`],
            );
            if (!keys.length) return;
            const dir = await ensureDirForSite();
            if (!dir) return;
            setBulkDownloading(true);
            const done: string[] = [];
            for (const k of keys) {
              const blobName = submittedData[`${k}_blob`] as string;
              const fileName = submittedData[k] as string;
              setDownloadStatus((s) => ({ ...s, [k]: "saving" }));
              try {
                const bytes = await fetchPhotoBytes(
                  process.env.REACT_APP_BACKEND_URL || "",
                  viewingSite.id,
                  k,
                  blobName,
                );
                await writeFileToDir(dir, downloadFileName(k, fileName), bytes);
                done.push(k);
                setDownloadStatus((s) => {
                  const next = { ...s };
                  delete next[k];
                  return next;
                });
              } catch (err) {
                console.error("Failed to download photo:", k, err);
                setDownloadStatus((s) => ({ ...s, [k]: "error" }));
              }
            }
            if (done.length) {
              setSiteDownloaded((prev) => {
                const next = new Set(prev);
                done.forEach((k) => next.add(k));
                return next;
              });
              persistDownloaded(done);
            }
            setBulkDownloading(false);
          };

          // Re-prompt for a folder so the admin can point downloads elsewhere.
          const changeDownloadFolder = async () => {
            if (!isFsAccessSupported()) {
              setVerifyConfirm({
                title: "Unsupported Browser",
                message:
                  "Saving photos to a folder needs Google Chrome or Microsoft Edge on desktop.",
                onConfirm: () => setVerifyConfirm(null),
                infoOnly: true,
              });
              return;
            }
            try {
              const handle = await pickPhotoDirectory();
              dirHandleRef.current = { siteId: viewingSite.id, handle };
            } catch {
              // user cancelled — keep the existing folder
            }
          };

          // The "Download Photos" + change-folder (📂) row shown at the bottom of
          // the photo section — reused in both the expanded card and the
          // collapsed footer.
          const renderPhotoBulkRow = (
            section: COPSection,
          ): React.ReactNode => {
            const keys = groupSectionPhotos(submittedData, section.id).flatMap(
              (g) => g.keys,
            );
            if (!keys.length) return null;
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  borderTop: "1px solid #2a2a2a",
                  paddingTop: 12,
                  marginTop: 8,
                }}
              >
                <span style={{ fontSize: "0.85rem", color: "#c9d1d9" }}>
                  Download Photos
                </span>
                <button
                  type="button"
                  className="smrtcop-btn-icon smrtcop-btn-view"
                  disabled={bulkDownloading}
                  style={{ opacity: bulkDownloading ? 0.6 : 1 }}
                  onClick={() => downloadAllPhotos(keys)}
                  title="Download all photos"
                >
                  {bulkDownloading ? <span className="smrtcop-spinner" /> : <DownloadIcon />}
                </button>
                <button
                  type="button"
                  className="smrtcop-btn-icon smrtcop-btn-view"
                  disabled={bulkDownloading}
                  style={{ opacity: bulkDownloading ? 0.6 : 1, fontSize: 15 }}
                  onClick={changeDownloadFolder}
                  title="Change download folder"
                >
                  📂
                </button>
              </div>
            );
          };

          // Reject (⛔) button — sits beside every copy icon. Grey to blend with
          // the copy button; turns red on hover (see .smrtcop-btn-reject). If the
          // item is already rejected it stays red and the tooltip shows the
          // reason. Click opens the resubmit-reason modal for this field.
          const renderRejectButton = (k: string) => {
            const rejected = submittedData[`${k}_status`] === "fail";
            const reason = submittedData[`${k}_statusReason`] as
              | string
              | undefined;
            return (
              <button
                type="button"
                className="smrtcop-btn-icon smrtcop-btn-reject"
                style={{
                  width: 28,
                  height: 28,
                  fontSize: 15,
                  ...(rejected ? { filter: "none" } : {}),
                }}
                title={
                  rejected
                    ? `Rejected — resubmit requested${reason ? `: ${reason}` : ""}`
                    : "Reject"
                }
                onClick={() => {
                  // Guardrail: a submitted site is frozen — the crew can't amend
                  // a rejected item until it's reopened. Block the reject and
                  // instruct the admin to reopen first (reopening clears the
                  // `submitted` snapshot, which unlocks this button).
                  if (hasSubmission) {
                    setVerifyConfirm({
                      title: "Reopen Site to Reject",
                      message:
                        "This site has already been submitted, so the crew can't amend anything until it's reopened. Please use 'Reopen Site' first — then you'll be able to reject items for resubmission.",
                      onConfirm: () => setVerifyConfirm(null),
                      infoOnly: true,
                    });
                    return;
                  }
                  setRejectReason(reason || "");
                  setRejectModal({ key: k });
                }}
              >
                ⛔
              </button>
            );
          };

          const renderPhotoLink = (k: string, label?: string) => {
            renderedPhotoKeys.add(k);
            const fileName = submittedData[k] as string;
            const display = label || fileName;
            const blobName = submittedData[`${k}_blob`] as string | undefined;
            const dataUrl = submittedData[`${k}_dataUrl`] as string | undefined; // legacy
            if (!blobName && !dataUrl) {
              return (
                <div key={k} style={{ color: "#9aa0a6" }}>
                  {display} (preview unavailable)
                </div>
              );
            }
            // Persisted ✓ once written to disk; transient "saving"/"error" else.
            const dstatus = downloadStatus[k];
            const downloaded = siteDownloaded.has(k) && !dstatus;
            return (
              <div
                key={k}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    color: "#0d6efd",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    if (blobName) openFile(k, blobName);
                    else window.open(dataUrl as string, "_blank");
                  }}
                >
                  {display}
                </span>
                {blobName && (
                  <button
                    type="button"
                    className="smrtcop-btn-icon"
                    style={{
                      width: 28,
                      height: 28,
                      fontSize: 15,
                      opacity: dstatus === "saving" ? 0.5 : 1,
                      color: downloaded
                        ? "#1faa59"
                        : dstatus === "error"
                          ? "#c62828"
                          : undefined,
                    }}
                    disabled={dstatus === "saving" || bulkDownloading}
                    onClick={() => downloadPhoto(k, blobName, fileName)}
                    title={dstatus === "error" ? "Download failed" : "Download Photo"}
                  >
                    {downloaded ? "✓" : dstatus === "error" ? "✕" : "⧉"}
                  </button>
                )}
                {blobName && renderRejectButton(k)}
              </div>
            );
          };

          const renderAdminField = (q: COPQuestion) => {
            const val = submittedData[q.key];
            const lbl = (
              <div style={labelSt}>
                {q.label}
                {q.required ? " *" : ""}
              </div>
            );
            if (q.type === "heading") {
              return (
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "#fff",
                    paddingTop: 6,
                    borderBottom: "1px solid #2a2a2a",
                    paddingBottom: 4,
                    textAlign: "center",
                  }}
                >
                  {q.label}
                </div>
              );
            }
            if (q.type === "certification") {
              return (
                <div
                  style={{
                    background: "#1a1a1a",
                    border: "1px solid #1faa59",
                    borderRadius: 8,
                    padding: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    textAlign: "center",
                  }}
                >
                  <div>
                    <span style={labelSt}>Certified By: </span>
                    <span style={{ color: "#fff" }}>
                      {submittedData[`${q.key}_certifiedBy`] || "—"}
                    </span>
                  </div>
                  <div>
                    <span style={labelSt}>Signature: </span>
                    <span style={{ color: "#1faa59" }}>
                      {submittedData[`${q.key}_signature`] || "—"}
                    </span>
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "#1faa59" }}>
                    {submittedData[`${q.key}_meta`]?.timestamp
                      ? `Signed: ${new Date(submittedData[`${q.key}_meta`].timestamp).toLocaleString()}`
                      : ""}
                  </div>
                </div>
              );
            }
            if (q.type === "checkbox") {
              const checked: string[] = val || [];
              return (
                <div style={{ textAlign: "center" }}>
                  {lbl}
                  <div style={{ color: "#fff" }}>
                    {checked.length ? "✓" : "—"}
                  </div>
                </div>
              );
            }
            if (q.type === "photo") {
              // Inline photo question inside a non-photo section — match the
              // base key plus any synthetic suffixes the wizard may append.
              const slots = Object.keys(submittedData)
                .filter(
                  (k) =>
                    (k === q.key ||
                      k.startsWith(`${q.key}__`) ||
                      k.startsWith(`${q.key}_photo_`)) &&
                    isPhotoEntry(k),
                )
                .sort();
              return (
                <div style={{ textAlign: "center" }}>
                  {lbl}
                  {slots.length === 0 ? (
                    <div style={{ color: "#555" }}>No file uploaded</div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      {slots.map((k) =>
                        renderPhotoLink(
                          k,
                          slots.length > 1
                            ? photoSlotLabel(q.label, k.slice(q.key.length))
                            : undefined,
                        ),
                      )}
                    </div>
                  )}
                  {/* FCC Registration photo: show the OCR'd (crew-verified)
                      registration number that drives the Tape Drop form's F26,
                      so the admin can confirm it before generating. */}
                  {/fcc/i.test(q.label || "") && (
                    <div style={{ marginTop: 4, color: "#fff", fontSize: "0.85rem" }}>
                      FCC Reg #:{" "}
                      {(submittedData[`${q.key}_fccNumber`] as string) || "—"}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div style={{ textAlign: "center" }}>
                {lbl}
                <div style={{ color: "#fff" }}>{val || "—"}</div>
              </div>
            );
          };

          return (
            <div
              style={{ maxWidth: "700px", margin: "0 auto", padding: "0 20px" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <div style={{ textAlign: "center", flex: 1 }}>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "1.2rem",
                      fontWeight: 700,
                      color: "#fff",
                    }}
                  >
                    {viewingSite.name} ({viewingSite.meta.siteId})
                    <span style={{ color: "#9aa0a6", fontWeight: 400 }}>
                      {" "}
                      (Admin View)
                    </span>
                  </h2>
                  {viewingSite.status === "Complete" && (
                    <div
                      style={{
                        fontSize: "0.72rem",
                        color: "#9aa0a6",
                        marginTop: 4,
                      }}
                    >
                      Submitted on{" "}
                      {new Date(viewingSite.updatedAt).toLocaleDateString(
                        "en-US",
                        { month: "long", day: "numeric", year: "numeric" },
                      )}{" "}
                      at{" "}
                      {new Date(viewingSite.updatedAt).toLocaleTimeString(
                        "en-US",
                        { hour: "2-digit", minute: "2-digit", hour12: false },
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="smrtcop-btn-action smrtcop-btn-warning"
                    style={{
                      flex: "none",
                      width: "auto",
                      padding: "0 12px",
                      height: 28,
                      fontSize: "0.78rem",
                    }}
                    disabled={viewingSite.status !== "Complete" || !certMeta}
                    title={
                      viewingSite.status !== "Complete" || !certMeta
                        ? "Site has not been submitted yet"
                        : "Reopen this site"
                    }
                    onClick={() => {
                      setReopenReason("");
                      setShowReopenModal(true);
                    }}
                  >
                    Reopen Site
                  </button>
                  <button
                    className="smrtcop-btn-action smrtcop-btn-cancel"
                    style={{
                      flex: "none",
                      width: "auto",
                      padding: "0 12px",
                      height: 28,
                      fontSize: "0.78rem",
                    }}
                    onClick={() => {
                      setViewingTemplate(null);
                      setViewingSite(null);
                    }}
                  >
                    Back
                  </button>
                </div>
              </div>

              {Object.keys(submittedData).length === 0 && (
                <div
                  className="smrtcop-card"
                  style={{ padding: 30, textAlign: "center", color: "#9aa0a6" }}
                >
                  No submission data found for this site.
                </div>
              )}

              {viewSections.map((section) => (
                <div
                  key={section.id}
                  className="template-card"
                  style={{
                    marginBottom: 16,
                    maxWidth: "700px",
                    marginLeft: "auto",
                    marginRight: "auto",
                  }}
                >
                  <div
                    className="template-card-header"
                    style={{ justifyContent: "center", cursor: "pointer" }}
                    onClick={() => toggleSection(section.id)}
                    title={
                      expandedSections.has(section.id) ? "Collapse" : "Expand"
                    }
                  >
                    <span
                      style={{
                        marginRight: 10,
                        color: "#9aa0a6",
                        fontSize: "1.35rem",
                        lineHeight: 1,
                        userSelect: "none",
                      }}
                    >
                      {expandedSections.has(section.id) ? "▾" : "▸"}
                    </span>
                    <div
                      className="template-card-title"
                      style={{ textAlign: "center" }}
                    >
                      {section.label}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    {!expandedSections.has(section.id) ? (
                      renderCollapsedFooter(section)
                    ) : !isStepRevealed(section.id) &&
                      section.type !== "photo" ? (
                      // Photo steps are the exception: the crew's photos autosave
                      // as they're captured, so the admin can review (and reject)
                      // them live without waiting for the step to be "saved".
                      <div
                        style={{
                          textAlign: "center",
                          color: "#9aa0a6",
                          fontSize: "0.85rem",
                          fontStyle: "italic",
                          padding: "8px 0",
                        }}
                      >
                        {"Awaiting crew — not yet completed"}
                      </div>
                    ) : section.type === "reports"
                      ? // Reports live at submitted[fieldKey] as {blobName,…}.
                        // RET = 6 TXTRPT uploads (P1/P4 side by side per sector,
                        // + Delta), then the Connected-RRH serial photos (3 per
                        // row), then the Alignment report. The ⬇ buttons are
                        // placeholders; the green Sent buttons drive history.
                        (() => {
                          const deltaAdded = isRetDeltaAdded(submittedData);
                          const positions = retPositionsOf(viewingSite?.meta);
                          const fileLink = {
                            color: "#0d6efd",
                            fontWeight: 600,
                            cursor: "pointer" as const,
                          };
                          const renderRef = (slot: ReportSlot) => {
                            const ref = submittedData[slot.fieldKey] as
                              | ReportFileRef
                              | undefined;
                            if (!ref || !ref.blobName) {
                              return <div style={{ color: "#555" }}>No file uploaded</div>;
                            }
                            // RET .txtrpt: the link opens the readable PDF
                            // (view/print/save from that tab); the copy icon puts
                            // the raw .txtrpt on the clipboard. The .txtrpt stays
                            // the source of truth for extraction.
                            if (isTxtrptSlot(slot.fieldKey)) {
                              const cs = copyStatus[slot.fieldKey];
                              return (
                                <div style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
                                  <span
                                    style={fileLink}
                                    title="View Report (PDF)"
                                    onClick={() => {
                                      const win = window.open("", "_blank");
                                      openTxtrptReportPdf(win, viewingSite.id, slot.fieldKey, ref).catch((err) => {
                                        console.error("Failed to open report PDF:", err);
                                        if (win) win.close();
                                      });
                                    }}
                                  >
                                    {ref.name}
                                  </span>
                                  <button
                                    type="button"
                                    className="smrtcop-btn-icon"
                                    style={{
                                      width: 28,
                                      height: 28,
                                      fontSize: 15,
                                      opacity: cs === "copying" ? 0.5 : 1,
                                      color: cs === "done" ? "#1faa59" : cs === "error" ? "#c62828" : undefined,
                                    }}
                                    disabled={cs === "copying"}
                                    title={cs === "error" ? "Copy failed" : "Copy TXTRPT to clipboard"}
                                    onClick={() => {
                                      setCopyStatus((s) => ({ ...s, [slot.fieldKey]: "copying" }));
                                      copyTxtrptText(viewingSite.id, slot.fieldKey, ref)
                                        .then(() => setCopyStatus((s) => ({ ...s, [slot.fieldKey]: "done" })))
                                        .catch((err) => { console.error("Failed to copy TXTRPT:", err); setCopyStatus((s) => ({ ...s, [slot.fieldKey]: "error" })); })
                                        .finally(() => setTimeout(() => setCopyStatus((s) => { const n = { ...s }; delete n[slot.fieldKey]; return n; }), 2000));
                                    }}
                                  >
                                    {cs === "done" ? "✓" : cs === "error" ? "✕" : "⧉"}
                                  </button>
                                  {renderRejectButton(slot.fieldKey)}
                                </div>
                              );
                            }
                            return (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                                <span
                                  style={fileLink}
                                  onClick={() => openFile(slot.fieldKey, ref.blobName)}
                                >
                                  {ref.name}
                                </span>
                                {renderRejectButton(slot.fieldKey)}
                              </span>
                            );
                          };
                          return (
                            <>
                              {/* RET TXTRPT uploads — P1/P4 side by side. */}
                              {retSectorRows(deltaAdded, positions).map((pair) => (
                                <div key={pair[0].fieldKey} style={{ display: "flex", gap: 14 }}>
                                  {pair.map((slot) => (
                                    <div key={slot.fieldKey} style={{ flex: 1, minWidth: 0, textAlign: "center" }}>
                                      <div style={labelSt}>{retSlotLabel(slot, positions)}</div>
                                      {renderRef(slot)}
                                    </div>
                                  ))}
                                </div>
                              ))}

                              {/* RRH Serial Numbers — 3 per row (P1 / P4 group per
                                  sector). Each shows the clickable photo (via
                                  renderPhotoLink) with the OCR serial below.
                                  Photos store the photo format (filename +
                                  `${key}_blob`); serial at `${key}_serial`. */}
                              <div style={{ borderTop: "1px solid #2a2a2a", paddingTop: 12, marginTop: 8 }}>
                                <div style={{ ...labelSt, color: "#c9d1d9", fontWeight: 600, marginBottom: 8, textAlign: "center" }}>
                                  RRH Serial Numbers
                                </div>
                                {rrhSectorsList(deltaAdded).map((sector) => {
                                  const all = rrhSlotsForSector(sector.key, positions);
                                  return (
                                    <div key={sector.key} style={{ marginBottom: 10 }}>
                                      <div style={labelSt}>{sector.label}</div>
                                      {[all.slice(0, 3), all.slice(3, 6)].filter((g) => g.length).map((group, gi) => (
                                        <div key={gi} style={{ display: "flex", gap: 10, marginTop: 4, alignItems: "flex-start" }}>
                                          {group.map((slot) => {
                                            const fileName = submittedData[slot.fieldKey] as string | undefined;
                                            const blobName = submittedData[`${slot.fieldKey}_blob`] as string | undefined;
                                            const hasFile = !!fileName && !!blobName;
                                            const serial = (submittedData[`${slot.fieldKey}_serial`] as string) || "";
                                            return (
                                              <div key={slot.fieldKey} style={{ flex: 1, minWidth: 0, textAlign: "center", fontSize: "0.78rem" }}>
                                                {hasFile ? (
                                                  renderPhotoLink(slot.fieldKey, rrhSlotLabel(slot, positions))
                                                ) : (
                                                  <div style={{ color: "#9aa0a6" }}>{rrhSlotLabel(slot, positions)} — no photo</div>
                                                )}
                                                <div style={{ color: hasFile && serial ? "#fff" : "#9aa0a6", marginTop: 2 }}>
                                                  {hasFile ? serial || "Pending…" : "N/A"}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Generate RET + RET Sent (one set) — below the
                                  RRH Serial Numbers section. */}
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, borderTop: "1px solid #2a2a2a", paddingTop: 12, marginTop: 8 }}>
                                <span style={{ fontSize: "0.85rem", color: "#c9d1d9" }}>AT&T RET Worksheet</span>
                                {exportAnchor(retOutputUrl, retGenStatus, setRetGenStatus, "Generate RET")}
                              </div>
                              {renderPortalConfirm("ret", "RET Sent")}
                            </>
                          );
                        })()
                      : section.type === "alignment"
                      ? // Alignment report (its own orderable section) +
                        // AN204 Sent. Lives at submitted[ALIGNMENT_SLOT.fieldKey].
                        (() => {
                          const ref = submittedData[ALIGNMENT_SLOT.fieldKey] as
                            | ReportFileRef
                            | undefined;
                          return (
                            <div style={{ textAlign: "center" }}>
                              <div style={labelSt}>{ALIGNMENT_SLOT.label}</div>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 4 }}>
                                {ref && ref.blobName ? (
                                  <>
                                    <span
                                      style={{ color: "#0d6efd", fontWeight: 600, cursor: "pointer" }}
                                      onClick={() => openFile(ALIGNMENT_SLOT.fieldKey, ref.blobName)}
                                    >
                                      {ref.name}
                                    </span>
                                    {renderRejectButton(ALIGNMENT_SLOT.fieldKey)}
                                  </>
                                ) : (
                                  <div style={{ color: "#555" }}>No file uploaded</div>
                                )}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, borderTop: "1px solid #2a2a2a", paddingTop: 12, marginTop: 8 }}>
                                <span style={{ fontSize: "0.85rem", color: "#c9d1d9" }}>AN204 Form</span>
                                {exportAnchor(an204OutputUrl, an204GenStatus, setAn204GenStatus, "Generate AN204")}
                              </div>
                              {renderPortalConfirm("alignment", "AN204 Sent")}
                            </div>
                          );
                        })()
                      : section.type === "jha"
                      ? // JHA — one PDF per day onsite at submitted[`jha_day_{n}`].
                        // Blue Generate RG123 (placeholder) + green RG123 Sent.
                        (() => {
                          return (
                            <div style={{ textAlign: "center" }}>
                              {jhaDaySlots(submittedData).map((slot) => {
                                const ref = submittedData[slot.fieldKey] as
                                  | ReportFileRef
                                  | undefined;
                                return (
                                  <div key={slot.fieldKey} style={{ marginBottom: 8 }}>
                                    <div style={labelSt}>{slot.label}</div>
                                    {ref && ref.blobName ? (
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                                        <span
                                          style={{ color: "#0d6efd", fontWeight: 600, cursor: "pointer" }}
                                          onClick={() => openFile(slot.fieldKey, ref.blobName)}
                                        >
                                          {ref.name}
                                        </span>
                                        {renderRejectButton(slot.fieldKey)}
                                      </span>
                                    ) : (
                                      <div style={{ color: "#555" }}>No file uploaded</div>
                                    )}
                                  </div>
                                );
                              })}
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, borderTop: "1px solid #2a2a2a", paddingTop: 12, marginTop: 8 }}>
                                <span style={{ fontSize: "0.85rem", color: "#c9d1d9" }}>RG123 Form</span>
                                {exportAnchor(rg123OutputUrl, rg123GenStatus, setRg123GenStatus, "Generate RG123")}
                              </div>
                              {renderPortalConfirm("rg123", "RG123 Sent")}
                            </div>
                          );
                        })()
                      : section.type === "bom"
                        ? // BOM table + actual-installed quantities persisted at
                          // submitted[`${section.id}_bom`], plus the verification
                          // confirmation flag.
                          (() => {
                            const rows =
                              (submittedData[`${section.id}_bom`] as any[]) ||
                              [];
                            const confirmed =
                              !!submittedData[`${section.id}_bomConfirmed`];
                            if (!rows.length) {
                              return (
                                <div
                                  style={{
                                    textAlign: "center",
                                    color: "#9aa0a6",
                                  }}
                                >
                                  No BOM data submitted
                                </div>
                              );
                            }
                            return (
                              <>
                                <table className="smrtcop-bom-table smrtcop-bom-view">
                                  <thead>
                                    <tr>
                                      <th>Item</th>
                                      <th>Description</th>
                                      <th>UOM</th>
                                      <th>Qty</th>
                                      <th>Actual Installed</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((r, i) => {
                                      const planned = String(
                                        r.plannedQty ?? "",
                                      ).trim();
                                      const actual = String(
                                        r.actualInstalled ?? "",
                                      ).trim();
                                      const diff =
                                        actual !== "" && actual !== planned;
                                      return (
                                        <tr key={i}>
                                          <td>{r.item}</td>
                                          <td>{r.description}</td>
                                          <td>{r.uom}</td>
                                          <td>{r.plannedQty}</td>
                                          <td
                                            className={
                                              diff
                                                ? "smrtcop-bom-diff"
                                                : undefined
                                            }
                                          >
                                            {r.actualInstalled || "—"}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 10,
                                    borderTop: "1px solid #2a2a2a",
                                    paddingTop: 12,
                                    marginTop: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "0.85rem",
                                      color: confirmed ? "#1faa59" : "#d4a017",
                                    }}
                                  >
                                    {confirmed
                                      ? "✓ Actual Installed Quantities Verified"
                                      : "Quantities not confirmed"}
                                  </span>
                                  {exportAnchor(
                                    bomOutputUrl,
                                    bomGenStatus,
                                    setBomGenStatus,
                                    "Generate BOM",
                                  )}
                                </div>
                                {renderPortalConfirm("bom", "BOM Sent")}
                              </>
                            );
                          })()
                        : section.type === "photo"
                          ? // Photo sections are generated from the checklist at
                            // wizard runtime; their captured photos live under
                            // `${section.id}_photo_…` (legacy) or `${section.id}_v2_…`
                            // (new Ericsson checklist) keys in the submitted data.
                            (() => {
                              // Group by sector (Alpha/Beta/Gamma) with centred
                              // headers, using the shared helper so the crew's
                              // View Submission and this admin view stay in sync.
                              const groups = groupSectionPhotos(
                                submittedData,
                                section.id,
                              );
                              if (!groups.length) {
                                return (
                                  <div
                                    style={{
                                      textAlign: "center",
                                      color: "#9aa0a6",
                                    }}
                                  >
                                    No photos uploaded
                                  </div>
                                );
                              }
                              return (
                                <>
                                  {groups.map((g) => (
                                    <div key={g.label}>
                                      <div
                                        style={{
                                          textAlign: "center",
                                          color: "#fff",
                                          fontWeight: 600,
                                          fontSize: "0.8rem",
                                          textTransform: "uppercase",
                                          letterSpacing: "0.04em",
                                          marginTop: 8,
                                          marginBottom: 4,
                                        }}
                                      >
                                        {g.label}
                                      </div>
                                      {g.keys.map((k) => (
                                        <div
                                          key={k}
                                          style={{ textAlign: "center" }}
                                        >
                                          {renderPhotoLink(k)}
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                  {/* Bulk download + change-folder, above the
                                      green "Photo's Sent" box. */}
                                  {renderPhotoBulkRow(section)}
                                  {renderPortalConfirm("photos", "Photo's Sent")}
                                </>
                              );
                            })()
                          : (
                              // Manual Q&A section. Hide conditional fields whose
                              // "show when" rule wasn't triggered, and append the
                              // Tape Drop generate + "sent" controls on TAPE DROP.
                              <>
                                {section.questions
                                  .filter((q) =>
                                    isFieldVisible(q, submittedData),
                                  )
                                  .map((q) => (
                                    <div key={q.key}>{renderAdminField(q)}</div>
                                  ))}
                                {(section.label || "")
                                  .trim()
                                  .toUpperCase() === "TAPE DROP" && (
                                  <>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 10,
                                        borderTop: "1px solid #2a2a2a",
                                        paddingTop: 12,
                                        marginTop: 8,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "0.85rem",
                                          color: "#c9d1d9",
                                        }}
                                      >
                                        RG114 Tape Drop Form
                                      </span>
                                      {exportAnchor(
                                        tapeDropOutputUrl,
                                        tapeGenStatus,
                                        setTapeGenStatus,
                                        "Generate Tape Drop",
                                      )}
                                    </div>
                                    {renderPortalConfirm(
                                      "tapeDrop",
                                      "Tape Drop Sent",
                                    )}
                                  </>
                                )}
                              </>
                            )}
                  </div>
                </div>
              ))}

              {/* Declaration */}
              {(() => {
                const declSection = viewingTemplate.sections.find((s) =>
                  s.label.toLowerCase().includes("declaration"),
                );
                if (!declSection) return null;
                const declQuestions = declSection.questions.filter(
                  (q) =>
                    q.type === "checkbox" && submittedData[q.key]?.length > 0,
                );
                return (
                  <div
                    className="template-card"
                    style={{
                      marginBottom: 16,
                      marginTop: 16,
                      maxWidth: "700px",
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}
                  >
                    <div
                      className="template-card-header"
                      style={{ justifyContent: "center" }}
                    >
                      <div
                        className="template-card-title"
                        style={{ textAlign: "center" }}
                      >
                        Declaration
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        padding: "0 12px",
                      }}
                    >
                      {declQuestions.map((q) => (
                        <div
                          key={q.key}
                          style={{ color: "#fff", fontSize: "0.9rem" }}
                        >
                          {q.label} –{" "}
                          <span style={{ color: "#1faa59" }}>Confirmed</span>
                        </div>
                      ))}
                      <div style={{ marginTop: 8 }}>
                        <div style={labelSt}>Certified By</div>
                        <div style={{ color: "#fff" }}>
                          {certMeta?.name || "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Certification Details */}
              {certMeta && (
                <div
                  style={{
                    background: "#1a1a1a",
                    border: "1px solid #1faa59",
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 16,
                    maxWidth: "700px",
                    marginLeft: "auto",
                    marginRight: "auto",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      color: "#1faa59",
                      marginBottom: 10,
                      textAlign: "center",
                    }}
                  >
                    Certification Details
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      textAlign: "center",
                    }}
                  >
                    <div>
                      <span style={labelSt}>Certified By: </span>
                      <span style={{ color: "#fff" }}>
                        {certMeta.name || "—"}
                      </span>
                    </div>
                    <div>
                      <span style={labelSt}>Signed: </span>
                      <span style={{ color: "#fff" }}>
                        {certMeta.timestamp
                          ? new Date(certMeta.timestamp).toLocaleString()
                          : "—"}
                      </span>
                    </div>
                    <div>
                      <span style={labelSt}>User ID: </span>
                      <span style={{ color: "#fff" }}>
                        {certMeta.userId || "—"}
                      </span>
                    </div>
                    <div>
                      <span style={labelSt}>IP Address: </span>
                      <span style={{ color: "#fff" }}>
                        {certMeta.ipAddress || "—"}
                      </span>
                    </div>
                    <div>
                      <span style={labelSt}>User Agent: </span>
                      <span style={{ color: "#9aa0a6", fontSize: "0.7rem" }}>
                        {certMeta.userAgent || "—"}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Previous Certifications — collapsible */}
              {certHistory.length > 0 && (
                <div
                  style={{
                    background: "#1a1a1a",
                    border: "1px solid #2a2a2a",
                    borderRadius: 8,
                    marginBottom: 16,
                    maxWidth: "700px",
                    marginLeft: "auto",
                    marginRight: "auto",
                  }}
                >
                  <div
                    style={{
                      padding: "12px 16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => setCertHistoryOpen(!certHistoryOpen)}
                  >
                    <span
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        color: "#9aa0a6",
                      }}
                    >
                      Previous Certifications ({certHistory.length})
                    </span>
                    <span style={{ color: "#9aa0a6", fontSize: "0.8rem" }}>
                      {certHistoryOpen ? "▲" : "▼"}
                    </span>
                  </div>
                  {certHistoryOpen && (
                    <div style={{ padding: "0 16px 16px" }}>
                      {certHistory
                        .slice()
                        .reverse()
                        .map((entry: any, i: number) => (
                          <div
                            key={i}
                            style={{
                              borderTop: i > 0 ? "1px solid #2a2a2a" : "none",
                              paddingTop: i > 0 ? 8 : 0,
                              marginTop: i > 0 ? 8 : 0,
                              display: "flex",
                              flexDirection: "column",
                              gap: 4,
                              textAlign: "center",
                            }}
                          >
                            <div>
                              <span style={labelSt}>Certified By: </span>
                              <span
                                style={{ color: "#fff", fontSize: "0.8rem" }}
                              >
                                {entry.name || "—"}
                              </span>
                            </div>
                            <div>
                              <span style={labelSt}>Signed: </span>
                              <span
                                style={{ color: "#fff", fontSize: "0.8rem" }}
                              >
                                {entry.timestamp
                                  ? new Date(entry.timestamp).toLocaleString()
                                  : "—"}
                              </span>
                            </div>
                            <div>
                              <span style={labelSt}>IP: </span>
                              <span
                                style={{
                                  color: "#9aa0a6",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {entry.ipAddress || "—"}
                              </span>
                            </div>
                            {entry.reopen_reason && (
                              <div>
                                <span style={labelSt}>Reopen Reason: </span>
                                <span
                                  style={{
                                    color: "#d4a017",
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  {entry.reopen_reason}
                                </span>
                              </div>
                            )}
                            {entry.reopened_at && (
                              <div>
                                <span style={labelSt}>Reopened: </span>
                                <span
                                  style={{
                                    color: "#9aa0a6",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {new Date(entry.reopened_at).toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

      {/* ══════════════ OVERVIEW / TEMPLATES ══════════════ */}
      {!isIngesting && !viewingTemplate && (
        <>
          <h1 className="smrtcop-title">Manage SMRT Cop</h1>

          {/* Traffic light summary */}
          <div
            style={{
              display: "flex",
              gap: 12,
              marginBottom: 14,
              maxWidth: "1100px",
              margin: "0 auto 14px",
            }}
          >
            <div
              title="Needs Admin Action"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#1c1c1c",
                border: "1px solid #2a2a2a",
                borderRadius: 6,
                padding: "4px 12px",
                cursor: "pointer",
              }}
              onClick={() => {
                setTrafficFilter("red");
                setIsTrafficModalOpen(true);
              }}
            >
              <span style={{ fontSize: "1rem" }}>🔴</span>
              <span
                style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}
              >
                {getRedItems(sites).length}
              </span>
            </div>
            <div
              title="Awaiting Action"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#1c1c1c",
                border: "1px solid #2a2a2a",
                borderRadius: 6,
                padding: "4px 12px",
                cursor: "pointer",
              }}
              onClick={() => {
                setTrafficFilter("yellow");
                setIsTrafficModalOpen(true);
              }}
            >
              <span style={{ fontSize: "1rem" }}>🟡</span>
              <span
                style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}
              >
                {getYellowItems(sites).length}
              </span>
            </div>
            <div
              title="Approved"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#1c1c1c",
                border: "1px solid #2a2a2a",
                borderRadius: 6,
                padding: "4px 12px",
                cursor: "pointer",
              }}
              onClick={() => {
                setTrafficFilter("green");
                setIsTrafficModalOpen(true);
              }}
            >
              <span style={{ fontSize: "1rem" }}>🟢</span>
              <span
                style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}
              >
                {getGreenItems(sites).length}
              </span>
            </div>
          </div>

          {/* Tab bar — fixed tabs + default template + "+" on top, manually
              built templates on a second row behind the triangle. */}
          <div className="smrtcop-tab-rows">
            <div className="smrtcop-tabs">
              <div
                className={`smrtcop-tab${activeTopTab === "overview" ? " smrtcop-tab-active" : ""}`}
                onClick={() => setActiveTopTab("overview")}
              >
                Open
              </div>
              <div
                className={`smrtcop-tab${activeTopTab === "approved" ? " smrtcop-tab-active" : ""}`}
                onClick={() => setActiveTopTab("approved")}
              >
                Complete
              </div>
              <div
                className={`smrtcop-tab${activeTopTab === "log" ? " smrtcop-tab-active" : ""}`}
                onClick={() => setActiveTopTab("log")}
              >
                Log
              </div>
              <div
                className={`smrtcop-tab${activeTopTab === "rules" ? " smrtcop-tab-active" : ""}`}
                onClick={() => setActiveTopTab("rules")}
              >
                Rules
              </div>
              {defaultTemplateTabs.map(({ tpl, index }) =>
                renderTemplateTab(tpl, index),
              )}
              <div
                className="smrtcop-tab smrtcop-tab-plus"
                onClick={handleAddTemplate}
              >
                +
              </div>

              {/* Only offered when there's a row to open — a triangle that
                  reveals nothing is worse than no triangle. */}
              {customTemplateTabs.length > 0 && (
                <button
                  className="smrtcop-tabs-toggle"
                  title={
                    templateTabsOpen
                      ? "Hide added templates"
                      : `Show added templates (${customTemplateTabs.length})`
                  }
                  aria-expanded={templateTabsOpen}
                  onClick={() => setTemplateTabsOpen((open) => !open)}
                >
                  {templateTabsOpen ? "▾" : "▸"}
                  {/* Only while closed — an open row counts itself. */}
                  {!templateTabsOpen && (
                    <span className="smrtcop-tab-count">
                      {customTemplateTabs.length}
                    </span>
                  )}
                </button>
              )}
            </div>

            {/* Kept mounted so it can animate shut as well as open; the wrapper
                holds it at zero height until the class says otherwise. */}
            {customTemplateTabs.length > 0 && (
              <div
                className={`smrtcop-tabs-reveal${templateTabsOpen ? " open" : ""}`}
                aria-hidden={!templateTabsOpen}
              >
                <div className="smrtcop-tabs smrtcop-tabs-custom">
                  {customTemplateTabs.map(({ tpl, index }) =>
                    renderTemplateTab(tpl, index),
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Log content (rejections + approval events) ── */}
          {activeTopTab === "log" && !viewingTemplate && (
            <div
              className="smrtcop-card"
              style={{ maxWidth: "1100px", margin: "0 auto" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <h3 style={{ margin: 0, fontSize: "1rem" }}>Site Log</h3>
                <span style={{ fontSize: "0.78rem", color: "#9aa0a6" }}>
                  {rejectionsLoading
                    ? "Loading…"
                    : `${logRows.length} entr${logRows.length === 1 ? "y" : "ies"}`}
                </span>
              </div>

              {rejectionsError && (
                <div
                  style={{
                    color: "#ef9a9a",
                    background: "rgba(229, 57, 53, 0.12)",
                    padding: "8px 10px",
                    borderRadius: 4,
                    marginBottom: 12,
                    fontSize: "0.85rem",
                  }}
                >
                  {rejectionsError}
                </div>
              )}

              {!rejectionsLoading && logRows.length === 0 && !rejectionsError && (
                <div
                  style={{
                    color: "#9aa0a6",
                    fontSize: "0.85rem",
                    textAlign: "center",
                    padding: "20px 0",
                  }}
                >
                  Nothing logged yet. Rejections sent back for resubmission, and
                  client approvals granted or reopened, are recorded here.
                </div>
              )}

              {logRows.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "0.82rem",
                      color: "#e4e4e4",
                    }}
                  >
                    <thead>
                      <tr style={{ textAlign: "left", color: "#9aa0a6" }}>
                        {["When", "Event", "Site", "By", "Sent To", "Item", "Details"].map(
                          (h) => (
                            <th
                              key={h}
                              style={{
                                padding: "8px 10px",
                                borderBottom: "1px solid #2a2a2a",
                                whiteSpace: h === "Details" ? "normal" : "nowrap",
                              }}
                            >
                              {h}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {logRows.map((row) => (
                        <tr key={row.id} style={{ borderBottom: "1px solid #222" }}>
                          <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                            {row.at ? new Date(row.at).toLocaleString() : "—"}
                          </td>
                          <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                            <span
                              style={{
                                color:
                                  row.tone === "approve"
                                    ? "#81c784"
                                    : row.tone === "reopen"
                                      ? "#ffb74d"
                                      : "#ef9a9a",
                              }}
                            >
                              {row.event}
                            </span>
                          </td>
                          <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                            {row.siteLabel}
                          </td>
                          <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                            {row.by}
                            {row.system && (
                              <span
                                style={{
                                  marginLeft: 6,
                                  fontSize: "0.68rem",
                                  color: "#9aa0a6",
                                  border: "1px solid #2a2a2a",
                                  borderRadius: 3,
                                  padding: "0 4px",
                                }}
                              >
                                system
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                            {row.sentTo}
                          </td>
                          <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                            {row.item}
                          </td>
                          <td style={{ padding: "8px 10px" }}>{row.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Rules registry content ── */}
          {activeTopTab === "rules" && !viewingTemplate && (
            <div
              className="smrtcop-card"
              style={{ maxWidth: "1100px", margin: "0 auto" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <h3 style={{ margin: 0, fontSize: "1rem" }}>Validation Rules</h3>
                <span style={{ fontSize: "0.78rem", color: "#9aa0a6" }}>
                  {rulesLoading
                    ? "Loading…"
                    : `${rules.length} rule${rules.length === 1 ? "" : "s"}`}
                </span>
              </div>

              {rulesError && (
                <div
                  style={{
                    color: "#ef9a9a",
                    background: "rgba(229, 57, 53, 0.12)",
                    padding: "8px 10px",
                    borderRadius: 4,
                    marginBottom: 12,
                    fontSize: "0.85rem",
                  }}
                >
                  {rulesError}
                </div>
              )}

              {!rulesLoading && rules.length === 0 && !rulesError && (
                <div
                  style={{
                    color: "#9aa0a6",
                    fontSize: "0.85rem",
                    textAlign: "center",
                    padding: "20px 0",
                  }}
                >
                  No rules defined yet.
                </div>
              )}

              {rules.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "0.85rem",
                    }}
                  >
                    <thead>
                      <tr style={{ textAlign: "left", color: "#9aa0a6" }}>
                        <th style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                          Rule
                        </th>
                        <th style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                          Applies To
                        </th>
                        <th style={{ padding: "8px 10px" }}>What it checks</th>
                        <th style={{ padding: "8px 10px" }}>On failure</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rules.map((r) => (
                        <tr key={r.id} style={{ borderBottom: "1px solid #222" }}>
                          <td
                            style={{
                              padding: "8px 10px",
                              whiteSpace: "nowrap",
                              color: "#fff",
                              verticalAlign: "top",
                            }}
                          >
                            {r.name}
                            <span
                              style={{
                                marginLeft: 6,
                                fontSize: "0.68rem",
                                color: "#9aa0a6",
                                border: "1px solid #2a2a2a",
                                borderRadius: 3,
                                padding: "0 4px",
                              }}
                            >
                              {r.category}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: "8px 10px",
                              whiteSpace: "nowrap",
                              verticalAlign: "top",
                            }}
                          >
                            {r.appliesTo}
                          </td>
                          <td style={{ padding: "8px 10px", verticalAlign: "top" }}>
                            {r.description}
                          </td>
                          <td style={{ padding: "8px 10px", verticalAlign: "top" }}>
                            {r.action}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Alignment tool calibration — manual re-scan. The rule fires on
                  upload and again each morning, but reports uploaded before the
                  rule shipped have never been read, so this seeds them. */}
              <div
                style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: "1px solid #2a2a2a",
                }}
              >
                <h4 style={{ margin: "0 0 6px", fontSize: "0.92rem" }}>
                  Alignment Tool Calibration
                </h4>
                <div
                  style={{
                    color: "#9aa0a6",
                    fontSize: "0.82rem",
                    marginBottom: 10,
                  }}
                >
                  Re-reads every Antenna Alignment Report on file, identifies the
                  tool that produced each one by serial number, and raises one
                  task per tool that is out of calibration or within 90 days of
                  it. Preview writes nothing.
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <button
                    className="smrtcop-btn-primary"
                    disabled={!!scanning}
                    onClick={() => runToolScan(false)}
                  >
                    {scanning === "preview" ? (
                      <>
                        <span className="smrtcop-spinner" /> Scanning…
                      </>
                    ) : (
                      "Preview scan"
                    )}
                  </button>
                  <button
                    className="smrtcop-btn-primary"
                    disabled={!!scanning}
                    style={{ background: "#1b5e20", borderColor: "#2e7d32" }}
                    onClick={() => runToolScan(true)}
                  >
                    {scanning === "apply" ? (
                      <>
                        <span className="smrtcop-spinner" /> Running…
                      </>
                    ) : (
                      "Scan & raise tasks"
                    )}
                  </button>
                </div>

                {scanError && (
                  <div
                    style={{
                      color: "#ef9a9a",
                      background: "rgba(229, 57, 53, 0.12)",
                      padding: "8px 10px",
                      borderRadius: 4,
                      fontSize: "0.85rem",
                      marginBottom: 10,
                    }}
                  >
                    {scanError}
                  </div>
                )}

                {scanResult && (
                  <div style={{ fontSize: "0.82rem" }}>
                    {scanResult.aborted ? (
                      <div
                        style={{
                          color: "#ef9a9a",
                          background: "rgba(229, 57, 53, 0.12)",
                          padding: "8px 10px",
                          borderRadius: 4,
                          marginBottom: 10,
                        }}
                      >
                        {scanResult.aborted}
                      </div>
                    ) : (
                      <div style={{ color: "#9aa0a6", marginBottom: 8 }}>
                        {scanResult.reports} report(s) read ·{" "}
                        {scanResult.tools.length} distinct tool(s)
                        {scanResult.unreadable
                          ? ` · ${scanResult.unreadable} unreadable`
                          : ""}
                        {scanResult.apply ? "" : " · preview, nothing written"}
                      </div>
                    )}

                    {scanResult.tools.length > 0 && (
                      <div style={{ overflowX: "auto", marginBottom: 10 }}>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            color: "#e4e4e4",
                          }}
                        >
                          <thead>
                            <tr style={{ textAlign: "left", color: "#9aa0a6" }}>
                              {["Serial", "Calibration due", "Last site", "Crew lead", "Used on", "Outcome"].map(
                                (h) => (
                                  <th
                                    key={h}
                                    style={{
                                      padding: "6px 10px",
                                      borderBottom: "1px solid #2a2a2a",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {h}
                                  </th>
                                ),
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {scanResult.tools.map((t) => {
                              const ev = scanResult.evaluation.find(
                                (e) => e.serial === t.serial,
                              );
                              const overdue = (ev?.days ?? 0) < 0;
                              return (
                                <tr
                                  key={t.serial}
                                  style={{ borderBottom: "1px solid #222" }}
                                >
                                  <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
                                    {t.serial}
                                  </td>
                                  <td
                                    style={{
                                      padding: "6px 10px",
                                      whiteSpace: "nowrap",
                                      color: overdue ? "#ef9a9a" : undefined,
                                    }}
                                  >
                                    {t.due}
                                  </td>
                                  <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
                                    {t.site}
                                  </td>
                                  <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
                                    {t.lead}
                                  </td>
                                  <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
                                    {t.usedOn}
                                  </td>
                                  <td style={{ padding: "6px 10px" }}>
                                    {ev?.action || "—"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {scanResult.rows.some((r) => r.status.startsWith("ERROR")) && (
                      <details style={{ color: "#9aa0a6" }}>
                        <summary style={{ cursor: "pointer" }}>
                          Reports that could not be read
                        </summary>
                        <ul style={{ margin: "6px 0 0 18px" }}>
                          {scanResult.rows
                            .filter((r) => r.status !== "" && !r.serial)
                            .map((r, i) => (
                              <li key={i}>
                                {r.site} — {r.status}
                              </li>
                            ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Overview content ── */}
          {activeTopTab === "overview" && !viewingTemplate && (
            <div
              className="smrtcop-card"
              style={{ maxWidth: "1100px", margin: "0 auto" }}
            >
              <div style={{ marginBottom: "12px" }}>
                <button
                  className="smrtcop-btn-primary"
                  onClick={handleStartCreate}
                >
                  + Add Site
                </button>
              </div>

              <div
                className="smrtcop-overview-header"
                style={{
                  gridTemplateColumns: "1.5fr 1.8fr 1.2fr 1.5fr 1.8fr 1.4fr",
                }}
              >
                <div>Site ID</div>
                <div>Assigned Template</div>
                <div>Crew Lead</div>
                <div>Status</div>
                <div>Last Updated</div>
                <div>Actions</div>
              </div>

              {openSites.length === 0 && (
                <div style={{ padding: "20px", color: "#9aa0a6" }}>
                  {sites.length === 0
                    ? 'No sites yet. Click "+ Add Site" to ingest your first site.'
                    : "No sites in progress — every site has been client approved. See the Complete tab."}
                </div>
              )}

              {openSites.map((site) => (
                <div
                  key={site.id}
                  className="smrtcop-overview-row"
                  style={{
                    gridTemplateColumns: "1.5fr 1.8fr 1.2fr 1.5fr 1.8fr 1.4fr",
                  }}
                >
                  <div>{site.meta.siteId}</div>
                  <div>
                    <select
                      className="smrtcop-select"
                      value={site.templateId}
                      disabled={["Complete", "Submitted", "Approved"].includes(
                        site.status,
                      )}
                      onChange={(e) => {
                        let changed: COPSite | undefined;
                        const updated = sites.map((s) => {
                          if (s.id !== site.id) return s;
                          changed = {
                            ...s,
                            templateId: e.target.value,
                            updatedAt: new Date().toISOString(),
                          };
                          return changed;
                        });
                        setSites(updated);
                        if (changed) putSiteRemote(changed);
                      }}
                    >
                      <option value="">— None —</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ fontSize: "0.85rem", color: "#ccc" }}>
                    {(() => {
                      // Show the crew lead only (assignedUserIds now also holds
                      // the wider Tower Crew). Fall back to the first assignee
                      // for sites that predate crewLeadId.
                      const leadId =
                        site.crewLeadId || site.assignedUserIds?.[0];
                      if (!leadId) return "—";
                      return users.find((u) => u.id === leadId)?.name || leadId;
                    })()}
                  </div>
                  <div
                    className={`smrtcop-status ${progressStatusFor(site)
                      .toLowerCase()
                      .replace(/ /g, "-")}`}
                  >
                    {progressStatusFor(site)}
                  </div>
                  <div>{new Date(site.updatedAt).toLocaleString()}</div>
                  <div className="smrtcop-actions">
                    {/* Always-visible: View + History. The ⋮ toggle at the end
                        expands the remaining actions inline (Review, Edit Crew,
                        Approve, Delete); clicking it again collapses them. */}
                    <button
                      className="smrtcop-btn-icon smrtcop-btn-view"
                      title="View"
                      onClick={() => {
                        if (!site.templateId) {
                          alert("No template assigned to this site.");
                          return;
                        }
                        const tpl = templates.find(
                          (t) => t.id === site.templateId,
                        );
                        if (!tpl) {
                          alert("Assigned template not found.");
                          return;
                        }
                        setViewingTemplate(tpl);
                        setViewingSite(site);
                      }}
                    >
                      👁
                    </button>
                    <button
                      className="smrtcop-btn-icon smrtcop-btn-history"
                      title="History"
                      onClick={() => setHistorySite(site)}
                    >
                      <span style={{ fontSize: "20px", lineHeight: 1 }}>↻</span>
                    </button>
                    {openActionMenu === site.id && (
                      // Collapse the ⋮ group once an action is used, so
                      // returning from (say) the Wizard Viewer doesn't leave the
                      // row expanded. Done with one wrapper rather than a call
                      // in each handler so any action added later inherits it;
                      // the click bubbles up AFTER the button's own handler has
                      // run. `display: contents` keeps the buttons as direct
                      // children of the actions row, so the layout is unchanged.
                      <div
                        style={{ display: "contents" }}
                        onClick={() => setOpenActionMenu(null)}
                      >
                        {/* Wizard Viewer — the crew's own wizard, read-only, as
                            the site's crew lead. The submitted-photo list can
                            only show photos that EXIST, so it reads as complete
                            when required slots are still empty; this shows the
                            gaps the way the crew sees them.

                            Hidden once the site is submitted: submit moves the
                            data from "answers" to "submitted" and clears
                            "answers", which is what the wizard renders — so a
                            Complete site would open BLANK and read as though the
                            crew did nothing. Review is the tool for a submitted
                            package; Reopen puts a site back into the wizard. */}
                        {site.status !== "Complete" && (
                          <button
                            className="smrtcop-btn-icon smrtcop-btn-wizard-view"
                            title="Wizard Viewer (read-only)"
                            onClick={() => {
                              if (!site.templateId) {
                                alert("No template assigned to this site.");
                                return;
                              }
                              setWizardViewerSiteId(site.id);
                            }}
                          >
                            <EyeIcon />
                          </button>
                        )}
                        <button
                          className="smrtcop-btn-icon smrtcop-btn-edit"
                          title="Review"
                          onClick={() => handleEditSite(site)}
                        >
                          ✎
                        </button>
                        {(site.status === "Assign Crew" ||
                          site.status === "Active") && (
                          <button
                            className="smrtcop-btn-icon smrtcop-btn-assign-crew"
                            title={
                              site.status === "Active"
                                ? "Edit Crew"
                                : "Assign Crew"
                            }
                            onClick={() => {
                              if (!site.templateId) {
                                setVerifyConfirm({
                                  title: "Template Required",
                                  message:
                                    "Please assign a template to this site before assigning crew.",
                                  onConfirm: () => setVerifyConfirm(null),
                                  infoOnly: true,
                                });
                                return;
                              }
                              setAssigningSiteId(site.id);
                              setShowAssignModal(true);
                            }}
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="white"
                            >
                              <path d="M16 11c1.66 0 3-1.34 3-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                            </svg>
                          </button>
                        )}
                        <button
                          className="smrtcop-btn-icon smrtcop-btn-delete"
                          title="Delete"
                          onClick={() => handleDeleteSite(site.id)}
                        >
                          🗑
                        </button>
                      </div>
                    )}

                    <button
                      className="smrtcop-btn-icon"
                      title={
                        openActionMenu === site.id
                          ? "Hide actions"
                          : "More actions"
                      }
                      aria-expanded={openActionMenu === site.id}
                      onClick={() =>
                        setOpenActionMenu((cur) =>
                          cur === site.id ? null : site.id,
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <span style={{ fontSize: "20px", lineHeight: 1 }}>⋮</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Complete content (tab value "approved") ── */}
          {/* Client-approved sites, filed by the year the approval landed.
              Everything here is frozen server-side (423 on any write), so the
              row offers read-only actions plus the admin's way back in. */}
          {activeTopTab === "approved" && !viewingTemplate && (
            <div
              className="smrtcop-card"
              style={{ maxWidth: "1100px", margin: "0 auto" }}
            >
              {approvedSites.length === 0 ? (
                <div
                  style={{
                    padding: "20px",
                    color: "#9aa0a6",
                    fontSize: "0.85rem",
                    textAlign: "center",
                  }}
                >
                  No approved sites yet. A site lands here once a COP admin
                  completes its "Confirm Client Approval" task in SMRT Tasks.
                </div>
              ) : (
                <>
                  {/* Year sub-tabs */}
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      marginBottom: 14,
                      borderBottom: "1px solid #2a2a2a",
                      paddingBottom: 8,
                    }}
                  >
                    {approvedYears.map((yr) => {
                      const active = yr === shownApprovedYear;
                      const count = approvedSites.filter(
                        (s) => approvalYear(s) === yr,
                      ).length;
                      return (
                        <div
                          key={yr}
                          onClick={() => setApprovedYear(yr)}
                          style={{
                            cursor: "pointer",
                            padding: "4px 12px",
                            borderRadius: 4,
                            fontSize: "0.85rem",
                            fontWeight: active ? 600 : 400,
                            color: active ? "#fff" : "#9aa0a6",
                            background: active ? "#2a2a2a" : "transparent",
                            border: "1px solid",
                            borderColor: active ? "#3a3a3a" : "transparent",
                          }}
                        >
                          {yr}
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: "0.7rem",
                              color: "#9aa0a6",
                            }}
                          >
                            {count}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div
                    className="smrtcop-overview-header"
                    style={{
                      gridTemplateColumns: "1.5fr 1.8fr 1.2fr 1.5fr 1.8fr 1.4fr",
                    }}
                  >
                    <div>Site ID</div>
                    <div>Assigned Template</div>
                    <div>Crew Lead</div>
                    <div>Status</div>
                    <div>Approved</div>
                    <div>Actions</div>
                  </div>

                  {approvedSitesForYear.map((site) => (
                    <div
                      key={site.id}
                      className="smrtcop-overview-row"
                      style={{
                        gridTemplateColumns:
                          "1.5fr 1.8fr 1.2fr 1.5fr 1.8fr 1.4fr",
                      }}
                    >
                      <div>{site.meta.siteId}</div>
                      <div style={{ fontSize: "0.85rem", color: "#ccc" }}>
                        {templates.find((t) => t.id === site.templateId)?.name ||
                          "—"}
                      </div>
                      <div style={{ fontSize: "0.85rem", color: "#ccc" }}>
                        {(() => {
                          const leadId =
                            site.crewLeadId || site.assignedUserIds?.[0];
                          if (!leadId) return "—";
                          return (
                            users.find((u) => u.id === leadId)?.name || leadId
                          );
                        })()}
                      </div>
                      <div
                        className={`smrtcop-status ${progressStatusFor(site)
                          .toLowerCase()
                          .replace(/ /g, "-")}`}
                      >
                        🔒 {progressStatusFor(site)}
                      </div>
                      <div style={{ fontSize: "0.82rem" }}>
                        {site.history?.clientApproved?.at
                          ? new Date(
                              site.history.clientApproved.at,
                            ).toLocaleString()
                          : "—"}
                        {site.history?.clientApproved?.byName && (
                          <div
                            style={{ fontSize: "0.72rem", color: "#9aa0a6" }}
                          >
                            by {site.history.clientApproved.byName}
                          </div>
                        )}
                      </div>
                      <div className="smrtcop-actions">
                        <button
                          className="smrtcop-btn-icon smrtcop-btn-view"
                          title="View"
                          onClick={() => {
                            const tpl = templates.find(
                              (t) => t.id === site.templateId,
                            );
                            if (!tpl) {
                              alert("Assigned template not found.");
                              return;
                            }
                            setViewingTemplate(tpl);
                            setViewingSite(site);
                          }}
                        >
                          👁
                        </button>
                        <button
                          className="smrtcop-btn-icon smrtcop-btn-history"
                          title="History"
                          onClick={() => setHistorySite(site)}
                        >
                          <span style={{ fontSize: "20px", lineHeight: 1 }}>
                            ↻
                          </span>
                        </button>
                        <button
                          className="smrtcop-btn-icon"
                          title="Reopen Approval (unlocks the site)"
                          style={{
                            background: "#4e342e",
                            border: "1px solid #6d4c41",
                            borderRadius: 6,
                            color: "#fff",
                            cursor: "pointer",
                            width: 28,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                          }}
                          onClick={() => {
                            setReopenApprovalReason("");
                            setReopenApprovalSite(site);
                          }}
                        >
                          🔓
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* ── Templates content ── */}
          {activeTopTab === "template" && (
            <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
              {activeTemplate ? (
                <SMRTCOPTemplateEditor
                  template={activeTemplate as any}
                  editingSectionId={editingSectionId}
                  dirty={templateDirty}
                  onStartEdit={handleStartEdit}
                  onCancelEdit={handleCancelEdit}
                  onSaveEdit={handleSaveEdit}
                  onUpdateSectionLabel={handleUpdateSectionLabel}
                  onMoveSection={handleMoveSection}
                  onDeleteSection={handleDeleteSection}
                  onAddSection={handleAddSection}
                  onUpdateQuestion={handleUpdateQuestion as any}
                  onReorderQuestion={handleReorderQuestion}
                  onAddQuestion={handleAddQuestion}
                  onDeleteQuestion={handleDeleteQuestion}
                />
              ) : (
                <div
                  className="smrtcop-card"
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#9aa0a6",
                  }}
                >
                  No templates yet. Click "+" to create one.
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ══════════════ INGESTION ══════════════ */}
      {isIngesting && extractedSiteData && (
        <div className="smrtcop-ingest-fullscreen">
          {/* LEFT — viewer panel (66%) */}
          <div className="smrtcop-viewer">
            {/* Loading overlay — shown while handleEditSite hydrates blobs */}
            {isLoadingSiteFiles && (
              <div className="smrtcop-viewer-loading">
                <span className="smrtcop-btn-spinner" aria-hidden />
                <span>Loading site files…</span>
              </div>
            )}
            {/* Collapsible thumbnail strip */}
            {!thumbCollapsed && (
              <div className="smrtcop-thumb-strip">
                {/* ── Required Documents ── */}
                <div className="smrtcop-slot-group-title">
                  Required Documents
                </div>
                {COP_FIXED_DOCS.map((doc) => {
                  const fileIdx = ingestFiles.findIndex(
                    (f) => f.slot === "fixed" && f.docKey === doc.key,
                  );
                  const file = fileIdx >= 0 ? ingestFiles[fileIdx] : null;
                  const isActive =
                    fileIdx >= 0 && fileIdx === activeDocumentIndex;
                  return (
                    <div key={doc.key} className="smrtcop-slot">
                      <div className="smrtcop-slot-label">
                        {doc.label}
                        {doc.required ? (
                          <span className="smrtcop-slot-required"> *</span>
                        ) : (
                          <span className="smrtcop-slot-optional">
                            {" "}
                            (optional)
                          </span>
                        )}
                      </div>
                      {file ? (
                        (() => {
                          // Excel isn't previewed in-app, so its box is a real
                          // download anchor (navigational for stored files —
                          // the click pattern that survives RBI) instead of a
                          // synthetic-click handler.
                          const excelHref = isExcelFile(file)
                            ? externalFileHref(file)
                            : null;
                          const inner = (
                            <>
                              <span className="smrtcop-slot-box-name">
                                {file.name}
                              </span>
                              {!ingestLocked && (
                                <span
                                  className="smrtcop-slot-box-close"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    removeIngestFile(fileIdx);
                                  }}
                                  title="Remove"
                                >
                                  ×
                                </span>
                              )}
                            </>
                          );
                          return excelHref ? (
                            <a
                              className={`smrtcop-slot-box filled${isActive ? " active" : ""}`}
                              href={excelHref}
                              download={externalFileDownloadName(file)}
                              title={`Open ${file.name}`}
                            >
                              {inner}
                            </a>
                          ) : (
                            <div
                              className={`smrtcop-slot-box filled${isActive ? " active" : ""}`}
                              onClick={() => {
                                if (!isExcelFile(file))
                                  setActiveDocumentIndex(fileIdx);
                              }}
                              title={
                                isExcelFile(file)
                                  ? `Open ${file.name}`
                                  : `Preview ${file.name}`
                              }
                            >
                              {inner}
                            </div>
                          );
                        })()
                      ) : (
                        <label
                          className={`smrtcop-slot-box empty${ingestLocked ? " locked" : ""}`}
                          title={
                            ingestLocked
                              ? "Site is locked"
                              : `Upload ${doc.label}`
                          }
                        >
                          <input
                            type="file"
                            accept={doc.accept}
                            style={{ display: "none" }}
                            disabled={ingestLocked}
                            onChange={(e) => {
                              handleFixedSlotUpload(
                                doc.key,
                                doc.label,
                                e.target.files,
                              );
                              e.currentTarget.value = "";
                            }}
                          />
                          <span className="smrtcop-slot-box-placeholder">
                            Upload
                          </span>
                        </label>
                      )}
                    </div>
                  );
                })}

                {/* ── Additional Documents ── */}
                <div className="smrtcop-slot-group-title">
                  Additional Documents
                </div>
                <label
                  className="smrtcop-thumb-item smrtcop-thumb-add"
                  title={
                    ingestLocked ? "Site is locked" : "Add additional document"
                  }
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={COP_ADDITIONAL_ACCEPT}
                    style={{ display: "none" }}
                    disabled={ingestLocked}
                    onChange={(e) => {
                      handleAddAdditionalFiles(e.target.files);
                      e.currentTarget.value = "";
                    }}
                  />
                  + Add Document
                </label>
                {ingestFiles.map((f, i) => {
                  if (f.slot !== "additional") return null;
                  // Same anchor-for-Excel treatment as the fixed slots above.
                  const excelHref = isExcelFile(f) ? externalFileHref(f) : null;
                  const inner = (
                    <>
                      <span className="smrtcop-thumb-name" title={f.name}>
                        {f.name}
                      </span>
                      {!ingestLocked && (
                        <span
                          className="smrtcop-thumb-close"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeIngestFile(i);
                          }}
                        >
                          ×
                        </span>
                      )}
                    </>
                  );
                  return excelHref ? (
                    <a
                      key={`add-${i}`}
                      className={`smrtcop-thumb-item${i === activeDocumentIndex ? " active" : ""}`}
                      href={excelHref}
                      download={externalFileDownloadName(f)}
                    >
                      {inner}
                    </a>
                  ) : (
                    <div
                      key={`add-${i}`}
                      className={`smrtcop-thumb-item${i === activeDocumentIndex ? " active" : ""}`}
                      onClick={() => {
                        if (!isExcelFile(f)) setActiveDocumentIndex(i);
                      }}
                    >
                      {inner}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Collapse / expand toggle */}
            <button
              className="smrtcop-thumb-toggle"
              title={thumbCollapsed ? "Show pages" : "Hide pages"}
              onClick={() => setThumbCollapsed((c) => !c)}
            >
              {thumbCollapsed ? "›" : "‹"}
            </button>

            {/* Main preview */}
            <div className="smrtcop-main-preview">
              {renderDocumentPreview(activeFile)}
            </div>
          </div>

          {/* RIGHT — site info form (34%) */}
          <div className="smrtcop-review-form">
            {/* RFDS extraction in progress — the OCR call takes several seconds;
                this banner tells the user the site fields are being populated. */}
            {rfdsExtracting && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  marginBottom: 10,
                  background: "#12261c",
                  border: "1px solid #1faa59",
                  borderRadius: 6,
                  color: "#7ee0a6",
                  fontSize: "0.82rem",
                }}
              >
                <span className="smrtcop-btn-spinner" aria-hidden />
                <span>Reading RFDS — extracting site data…</span>
              </div>
            )}
            {/* Action buttons — top */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  className="smrtcop-btn-action smrtcop-btn-cancel"
                  style={{
                    flex: "none",
                    width: "auto",
                    padding: "0 12px",
                    height: 28,
                    fontSize: "0.78rem",
                  }}
                  onClick={handleCancelIngest}
                >
                  Cancel
                </button>
                <button
                  className="smrtcop-btn-action smrtcop-btn-save"
                  style={{
                    flex: "none",
                    width: "auto",
                    padding: "0 12px",
                    height: 28,
                    fontSize: "0.78rem",
                  }}
                  title="Click Save if you plan to Verify later"
                  disabled={isVerifying || ingestLocked || !canConfirm}
                  onClick={async () => {
                    if (isVerifying) return;
                    setIsVerifying(true);
                    try {
                      await handleSaveSite();
                    } finally {
                      setIsVerifying(false);
                    }
                  }}
                >
                  {isVerifying ? (
                    <>
                      <span className="smrtcop-btn-spinner" aria-hidden />
                      Saving…
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
              <button
                className={`smrtcop-btn-action ${ingestLocked ? "smrtcop-btn-locked" : "smrtcop-btn-confirm"}`}
                style={{
                  flex: "none",
                  width: "auto",
                  padding: "0 12px",
                  height: 28,
                  fontSize: "0.78rem",
                }}
                title={
                  ingestLocked
                    ? "Click to unlock and edit"
                    : !canConfirm
                      ? "Enter Site ID first"
                      : missingRequiredDocs.length > 0
                        ? `Upload required documents: ${missingRequiredDocs.join(", ")}`
                        : "Site Data must be Verified to proceed"
                }
                disabled={isVerifying || (!ingestLocked && !canVerify)}
                onClick={handleAdminReview}
              >
                {isVerifying ? (
                  <>
                    <span className="smrtcop-btn-spinner" aria-hidden />
                    Verifying…
                  </>
                ) : ingestLocked ? (
                  "🔒 Verify"
                ) : (
                  "Verify"
                )}
              </button>
            </div>

            <div className="smrtcop-review-form-grid">
              {/* Site ID | FA Location Code */}
              <div className="smrtcop-field">
                <label className="smrtcop-field-label">Site ID *</label>
                <input
                  className="smrtcop-input"
                  value={extractedSiteData.siteId}
                  disabled={ingestLocked}
                  onChange={(e) => updateExtracted("siteId", e.target.value)}
                />
              </div>
              <div className="smrtcop-field">
                <label className="smrtcop-field-label">FA Location Code</label>
                <input
                  className="smrtcop-input"
                  value={extractedSiteData.faLocationCode}
                  disabled={ingestLocked}
                  onChange={(e) =>
                    updateExtracted("faLocationCode", e.target.value)
                  }
                />
              </div>

              {/* Physical Address — full width */}
              <div className="smrtcop-field smrtcop-field-span">
                <label className="smrtcop-field-label">Physical Address</label>
                <input
                  className="smrtcop-input"
                  value={extractedSiteData.address}
                  disabled={ingestLocked}
                  onChange={(e) => updateExtracted("address", e.target.value)}
                />
              </div>

              {/* Co-ordinates (Lat/Long) — full width */}
              <div className="smrtcop-field smrtcop-field-span">
                <label className="smrtcop-field-label">
                  Co-ordinates (Lat/Long)
                </label>
                <input
                  className="smrtcop-input"
                  placeholder="e.g. 40.7128, -74.0060"
                  value={extractedSiteData.coordinates}
                  disabled={ingestLocked}
                  onChange={(e) =>
                    updateExtracted("coordinates", e.target.value)
                  }
                />
              </div>

              {/* Azimuths — full width, split into Sector A / B / C, plus an
                  optional Delta (D) sector added via the "+" control. */}
              <div className="smrtcop-field smrtcop-field-span">
                <label className="smrtcop-field-label">Azimuths</label>
                <div className="smrtcop-azimuth-row">
                  {(extractedSiteData.azimuths.D !== undefined
                    ? (["A", "B", "C", "D"] as const)
                    : (["A", "B", "C"] as const)
                  ).map((sector) => (
                    <div key={sector} className="smrtcop-azimuth-cell">
                      <div className="smrtcop-azimuth-sublabel">
                        Sector {sector}
                      </div>
                      <input
                        className="smrtcop-input"
                        placeholder="e.g. 70"
                        inputMode="numeric"
                        value={extractedSiteData.azimuths[sector] ?? ""}
                        disabled={ingestLocked}
                        onChange={(e) => updateAzimuth(sector, e.target.value)}
                      />
                    </div>
                  ))}
                  {/* Add / remove the Delta sector azimuth. */}
                  <div className="smrtcop-azimuth-add">
                    <button
                      type="button"
                      className="smrtcop-photo-row-add"
                      style={{ marginTop: 0 }}
                      disabled={ingestLocked}
                      onClick={
                        extractedSiteData.azimuths.D !== undefined
                          ? removeDeltaAzimuth
                          : addDeltaAzimuth
                      }
                      aria-label={
                        extractedSiteData.azimuths.D !== undefined
                          ? "Remove Delta"
                          : "Add Delta"
                      }
                      title={
                        extractedSiteData.azimuths.D !== undefined
                          ? "Remove Delta"
                          : "Add Delta"
                      }
                    >
                      {extractedSiteData.azimuths.D !== undefined ? "−" : "+"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Site Name | USID — two across on one line */}
              <div className="smrtcop-field-span smrtcop-field-double">
                <div className="smrtcop-field">
                  <label className="smrtcop-field-label">Site Name</label>
                  <input
                    className="smrtcop-input"
                    value={extractedSiteData.siteName}
                    disabled={ingestLocked}
                    onChange={(e) => updateExtracted("siteName", e.target.value)}
                  />
                </div>
                <div className="smrtcop-field">
                  <label className="smrtcop-field-label">USID</label>
                  <input
                    className="smrtcop-input"
                    value={extractedSiteData.usid}
                    disabled={ingestLocked}
                    onChange={(e) => updateExtracted("usid", e.target.value)}
                  />
                </div>
              </div>

              {/* IWM Job # | PTN — two across on the next line. Both come from
                  the Construction Drawings "IWM/ PTN:" header line. */}
              <div className="smrtcop-field-span smrtcop-field-double">
                <div className="smrtcop-field">
                  <label className="smrtcop-field-label">IWM Job #</label>
                  <input
                    className="smrtcop-input"
                    value={extractedSiteData.iwm}
                    disabled={ingestLocked}
                    onChange={(e) => updateExtracted("iwm", e.target.value)}
                  />
                </div>
                <div className="smrtcop-field">
                  <label className="smrtcop-field-label">PTN</label>
                  <input
                    className="smrtcop-input"
                    value={extractedSiteData.ptn}
                    disabled={ingestLocked}
                    onChange={(e) => updateExtracted("ptn", e.target.value)}
                  />
                </div>
              </div>

              {/* Finance Region | Finance Market. NOTE: the Finance Market box
                  is still the `market` field (unchanged key) — the Tape Drop
                  build reads `market`, so only the display label changed from
                  "Market" to "Finance Market". */}
              <div className="smrtcop-field">
                <label className="smrtcop-field-label">Finance Region</label>
                <input
                  className="smrtcop-input"
                  value={extractedSiteData.financeRegion}
                  disabled={ingestLocked}
                  onChange={(e) =>
                    updateExtracted("financeRegion", e.target.value)
                  }
                />
              </div>
              <div className="smrtcop-field">
                <label className="smrtcop-field-label">Finance Market</label>
                <input
                  className="smrtcop-input"
                  value={extractedSiteData.market}
                  disabled={ingestLocked}
                  onChange={(e) => updateExtracted("market", e.target.value)}
                />
              </div>

              {/* Finance Submarket | Project Name */}
              <div className="smrtcop-field">
                <label className="smrtcop-field-label">Finance Submarket</label>
                <input
                  className="smrtcop-input"
                  value={extractedSiteData.financeSubmarket}
                  disabled={ingestLocked}
                  onChange={(e) =>
                    updateExtracted("financeSubmarket", e.target.value)
                  }
                />
              </div>
              <div className="smrtcop-field">
                <label className="smrtcop-field-label">Project Name</label>
                <input
                  className="smrtcop-input"
                  value={extractedSiteData.projectName}
                  disabled={ingestLocked}
                  onChange={(e) =>
                    updateExtracted("projectName", e.target.value)
                  }
                />
              </div>

              {/* RET Positions — the antenna positions this site's RET scans
                  cover. Derived from the RFDS by comparing each Final antenna
                  model against its Existing one: only positions whose antenna is
                  actually being replaced need a RET report. Usually two, but ONE
                  when a single antenna changes. Drives the P-labels below, the
                  crew's RET/RRH slots, and the output's Column L. */}
              <div className="smrtcop-field smrtcop-field-span">
                <label className="smrtcop-field-label">RET Positions</label>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                  {extractedSiteData.retPositions.map((pos, idx) => (
                    <div key={idx} className="smrtcop-azimuth-cell">
                      <div className="smrtcop-azimuth-sublabel">
                        {idx === 0 ? "First" : "Second"}
                      </div>
                      <input
                        className="smrtcop-input"
                        inputMode="numeric"
                        placeholder="e.g. 1"
                        value={pos}
                        disabled={ingestLocked}
                        onChange={(e) =>
                          updateRetPosition(idx as 0 | 1, e.target.value)
                        }
                      />
                    </div>
                  ))}
                  {!ingestLocked && extractedSiteData.retPositions.length < 2 && (
                    <button
                      type="button"
                      className="smrtcop-btn-icon"
                      title="Add a second RET position"
                      onClick={addSecondRetPosition}
                      style={{ marginBottom: 4 }}
                    >
                      +
                    </button>
                  )}
                  {!ingestLocked && extractedSiteData.retPositions.length > 1 && (
                    <button
                      type="button"
                      className="smrtcop-btn-icon"
                      title="Single-RET site — remove the second position"
                      onClick={removeSecondRetPosition}
                      style={{ marginBottom: 4 }}
                    >
                      ×
                    </button>
                  )}
                </div>
                {extractedSiteData.retPositions.length < 2 && (
                  <div
                    className="smrtcop-azimuth-sublabel"
                    style={{ marginTop: 6 }}
                  >
                    Single-RET site — the crew uploads one RET report per sector.
                  </div>
                )}
              </div>

              {/* Controlling RET — per sector, one value per RET position.
                  Sourced from the RFDS "Cell Details Final" and verified here;
                  feeds the output's "Radio head controlling RET" column. The
                  P-labels reflect the RET Positions above. Delta follows the
                  azimuth Delta toggle. */}
              <div className="smrtcop-field smrtcop-field-span">
                <label className="smrtcop-field-label">Controlling RET</label>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(extractedSiteData.azimuths.D !== undefined
                    ? (["A", "B", "C", "D"] as const)
                    : (["A", "B", "C"] as const)
                  ).map((sector) => {
                    const cr =
                      extractedSiteData.controllingRets[sector] ?? { p1: "", p4: "" };
                    return (
                      <div
                        key={sector}
                        style={{ display: "flex", alignItems: "center", gap: 10 }}
                      >
                        <div
                          className="smrtcop-azimuth-sublabel"
                          style={{ width: 70, flex: "none" }}
                        >
                          Sector {sector}
                        </div>
                        {([["p1", 0], ["p4", 1]] as const)
                          .filter(
                            ([, posIdx]) =>
                              extractedSiteData.retPositions[posIdx] !== undefined,
                          )
                          .map(([port, posIdx]) => (
                          <div key={port} className="smrtcop-azimuth-cell">
                            <div className="smrtcop-azimuth-sublabel">
                              P{extractedSiteData.retPositions[posIdx]}
                            </div>
                            <input
                              className="smrtcop-input"
                              placeholder="RRH serial"
                              value={cr[port] ?? ""}
                              disabled={ingestLocked}
                              onChange={(e) =>
                                updateControllingRet(sector, port, e.target.value)
                              }
                            />
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* USEIDs (NEW) — from the RFDS CellDetails(Final), Status=NEW,
                  grouped by sector. Read-only review list so the verify tech
                  can confirm we captured the correct NEW cells before the AN204
                  (alignment) upload is generated. */}
              {(() => {
                const bySector = extractedSiteData.newUseIdsBySector || {};
                const sectors = Object.keys(bySector).sort();
                const total = sectors.reduce(
                  (n, s) => n + (bySector[s]?.length || 0),
                  0,
                );
                return (
                  <div className="smrtcop-field smrtcop-field-span">
                    <label className="smrtcop-field-label">
                      USEIDs (NEW){total ? ` — ${total}` : ""}
                    </label>
                    {total === 0 ? (
                      <div style={{ color: "#9aa0a6", fontSize: "0.85rem" }}>
                        None extracted — re-ingest the RFDS if this site has NEW
                        cells.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {sectors.map((sector) => (
                          <div
                            key={sector}
                            style={{ display: "flex", alignItems: "flex-start", gap: 10 }}
                          >
                            <div
                              className="smrtcop-azimuth-sublabel"
                              style={{ width: 70, flex: "none", paddingTop: 4 }}
                            >
                              Sector {sector}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                              {bySector[sector].map((useId) => (
                                <span
                                  key={useId}
                                  style={{
                                    fontFamily: "monospace",
                                    fontSize: "0.8rem",
                                    background: "#1c2128",
                                    border: "1px solid #30363d",
                                    borderRadius: 4,
                                    padding: "2px 6px",
                                    color: "#c9d1d9",
                                  }}
                                >
                                  {useId}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Scope of Work — full width, larger textarea */}
              <div className="smrtcop-field smrtcop-field-span">
                <label className="smrtcop-field-label">Scope of Work</label>
                <textarea
                  className="smrtcop-input smrtcop-input-textarea"
                  placeholder="Describe the scope of work for this site..."
                  value={extractedSiteData.scopeOfWork}
                  disabled={ingestLocked}
                  onChange={(e) =>
                    updateExtracted("scopeOfWork", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── New Template Modal ── */}
      {showNewTemplateModal && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Add Template</h2>
              <button
                className="btn-cancel"
                onClick={() => setShowNewTemplateModal(false)}
              >
                Close
              </button>
            </div>
            <div className="smrtdocs-modal-body">
              <div
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                {/* Option 1: Create blank */}
                <button
                  className="smrtcop-btn-action smrtcop-btn-confirm"
                  style={{ width: "100%", height: 40 }}
                  onClick={handleCreateBlankTemplate}
                >
                  + Create New Template
                </button>

                {/* Divider */}
                <div
                  style={{
                    textAlign: "center",
                    color: "#555",
                    fontSize: "0.8rem",
                  }}
                >
                  — or —
                </div>

                {/* Option 2: Duplicate */}
                <div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#9aa0a6",
                      marginBottom: 6,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Duplicate Existing Template
                  </div>
                  <select
                    className="smrtcop-input"
                    value={duplicateSourceId}
                    onChange={(e) => setDuplicateSourceId(e.target.value)}
                    style={{ width: "100%", height: 36, marginBottom: 10 }}
                  >
                    <option value="">— Select template —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <button
                    className="smrtcop-btn-action smrtcop-btn-save"
                    style={{ width: "100%", height: 40 }}
                    disabled={!duplicateSourceId}
                    onClick={handleDuplicateTemplate}
                  >
                    Duplicate
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Reopen Approval Modal ── */}
      {/* Lifting the client-approval lock. The reason is mandatory and is what
          lands in the Log tab — same contract as a sensitive-field reveal. */}
      {reopenApprovalSite && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Reopen Approval</h2>
              <button
                className="btn-cancel"
                onClick={() => setReopenApprovalSite(null)}
                disabled={reopenApprovalBusy}
              >
                Close
              </button>
            </div>
            <div className="smrtdocs-modal-body">
              <div
                style={{
                  marginBottom: 12,
                  color: "#9aa0a6",
                  fontSize: "0.85rem",
                }}
              >
                <strong style={{ color: "#fff" }}>
                  {reopenApprovalSite.name || reopenApprovalSite.meta.siteId}
                </strong>{" "}
                is locked as Client Approved. Reopening unlocks it for edits,
                returns it to the Open tab at "pending client approval", and
                cancels any open approval task. This is recorded in the Log.
              </div>
              <div
                style={{
                  marginBottom: 6,
                  fontSize: "0.75rem",
                  color: "#9aa0a6",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Reason for Reopening *
              </div>
              <textarea
                className="smrtcop-input"
                placeholder="Why is this approved site being reopened?"
                value={reopenApprovalReason}
                onChange={(e) => setReopenApprovalReason(e.target.value)}
                disabled={reopenApprovalBusy}
                style={{
                  width: "100%",
                  height: 80,
                  padding: "8px 10px",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div
              style={{
                padding: "12px 20px",
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                borderTop: "1px solid #555",
              }}
            >
              <button
                className="smrtcop-btn-action smrtcop-btn-cancel"
                style={{
                  flex: "none",
                  width: "auto",
                  padding: "0 12px",
                  height: 30,
                }}
                onClick={() => setReopenApprovalSite(null)}
                disabled={reopenApprovalBusy}
              >
                Cancel
              </button>
              <button
                className="smrtcop-btn-action smrtcop-btn-warning"
                style={{
                  flex: "none",
                  width: "auto",
                  padding: "0 12px",
                  height: 30,
                }}
                disabled={!reopenApprovalReason.trim() || reopenApprovalBusy}
                onClick={handleReopenApproval}
              >
                {reopenApprovalBusy ? (
                  <>
                    <span className="smrtcop-spinner" /> Reopening…
                  </>
                ) : (
                  "Reopen Approval"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reopen Site Modal ── */}
      {/* ── Status History modal ── */}
      {/* Wizard Viewer — the real crew wizard, read-only, filling the screen so
          it reads exactly as the tech's does. It renders its own shell and
          close control, so there is no modal chrome around it. */}
      {wizardViewerSiteId && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "#111",
            overflow: "auto",
          }}
        >
          <SMRTCOP
            viewSiteId={wizardViewerSiteId}
            onCloseViewer={() => setWizardViewerSiteId(null)}
          />
        </div>
      )}

      {historySite && (
        <div
          className="smrtdocs-modal-overlay"
          onClick={() => setHistorySite(null)}
        >
          <div
            className="smrtdocs-modal"
            style={{ width: "460px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Status History</h2>
              <button
                className="btn-cancel"
                onClick={() => setHistorySite(null)}
              >
                Close
              </button>
            </div>
            <div className="smrtdocs-modal-body">
              <div
                style={{
                  marginBottom: 14,
                  color: "#9aa0a6",
                  fontSize: "0.8rem",
                }}
              >
                {historySite.name} ·{" "}
                <span style={{ color: "#fff" }}>
                  {deriveProgressStatus(historySite.history)}
                </span>
              </div>
              <div className="smrtcop-timeline">
                {buildSiteTimeline(historySite.history).map((s) => (
                  <div
                    key={s.key}
                    className={`smrtcop-tl-step${s.done ? " smrtcop-tl-done" : ""}`}
                  >
                    <span className="smrtcop-tl-dot" />
                    <div className="smrtcop-tl-body">
                      <div className="smrtcop-tl-name">{s.label}</div>
                      <div className="smrtcop-tl-meta">
                        {s.done
                          ? `${s.byName ? `${s.byName} · ` : ""}${
                              s.at ? new Date(s.at).toLocaleString() : ""
                            }`
                          : "Pending"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showReopenModal && viewingSite && viewingTemplate && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Reopen Site</h2>
              <button
                className="btn-cancel"
                onClick={() => setShowReopenModal(false)}
              >
                Close
              </button>
            </div>
            <div className="smrtdocs-modal-body">
              <div
                style={{
                  marginBottom: 12,
                  color: "#9aa0a6",
                  fontSize: "0.85rem",
                }}
              >
                Reopening{" "}
                <strong style={{ color: "#fff" }}>{viewingSite.name}</strong>{" "}
                will allow the user to re-run the wizard and re-certify.
              </div>
              <div
                style={{
                  marginBottom: 6,
                  fontSize: "0.75rem",
                  color: "#9aa0a6",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Reason for Reopening *
              </div>
              <textarea
                className="smrtcop-input"
                placeholder="Enter reason for reopening this site..."
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                style={{
                  width: "100%",
                  height: 80,
                  padding: "8px 10px",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div
              style={{
                padding: "12px 20px",
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                borderTop: "1px solid #555",
              }}
            >
              <button
                className="smrtcop-btn-action smrtcop-btn-cancel"
                style={{
                  flex: "none",
                  width: "auto",
                  padding: "0 12px",
                  height: 30,
                }}
                onClick={() => setShowReopenModal(false)}
              >
                Cancel
              </button>
              <button
                className="smrtcop-btn-action smrtcop-btn-warning"
                style={{
                  flex: "none",
                  width: "auto",
                  padding: "0 12px",
                  height: 30,
                }}
                disabled={!reopenReason.trim()}
                onClick={() => {
                  // Archive current cert meta into history with reopen reason
                  const submitted = { ...viewingSiteState.submitted };
                  for (const sec of viewingTemplate.sections) {
                    for (const q of sec.questions) {
                      if (
                        q.type === "certification" &&
                        submitted[`${q.key}_meta`]
                      ) {
                        const history = submitted[`${q.key}_history`] || [];
                        history.push({
                          ...submitted[`${q.key}_meta`],
                          reopen_reason: reopenReason.trim(),
                          reopened_at: new Date().toISOString(),
                        });
                        submitted[`${q.key}_history`] = history;
                        delete submitted[`${q.key}_meta`];
                        delete submitted[`${q.key}_signature`];
                        delete submitted[`${q.key}_certifiedBy`];
                      }
                    }
                  }
                  // Save reopened state: answers = old submitted (with history),
                  // submitted = cleared, and DROP the template snapshot so the
                  // reopened site re-works against the live template (a fresh
                  // snapshot is taken when the crew re-submits).
                  fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/smrtcop/site-state/${encodeURIComponent(viewingSite.id)}`,
                    {
                      method: "PUT",
                      credentials: "include",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        answers: submitted,
                        submitted: {},
                        templateSnapshot: null,
                      }),
                    },
                  ).catch((err) =>
                    console.error("Failed to save reopened state:", err),
                  );

                  let reopened: COPSite | undefined;
                  const updatedSites = sites.map((s) => {
                    if (s.id !== viewingSite.id) return s;
                    reopened = {
                      ...s,
                      status: "Active" as COPSite["status"],
                      updatedAt: new Date().toISOString(),
                    };
                    return reopened;
                  });
                  setSites(updatedSites);
                  if (reopened) putSiteRemote(reopened);

                  setShowReopenModal(false);
                  setViewingTemplate(null);
                  setViewingSite(null);
                }}
              >
                Confirm Reopen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject / Request Resubmission Modal ── */}
      {rejectModal && viewingSite && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Request Resubmission</h2>
              <button
                className="btn-cancel"
                onClick={() => {
                  setRejectModal(null);
                  setRejectReason("");
                }}
              >
                Close
              </button>
            </div>
            <div className="smrtdocs-modal-body">
              <div
                style={{
                  marginBottom: 12,
                  color: "#c9d1d9",
                  fontSize: "0.85rem",
                }}
              >
                Please provide a clear explanation of why this needs to be
                resubmitted.
              </div>
              <textarea
                className="smrtcop-input"
                placeholder="Explain what's wrong and what the crew needs to resubmit..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                autoFocus
                style={{
                  width: "100%",
                  height: 90,
                  padding: "8px 10px",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div
              style={{
                padding: "12px 20px",
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                borderTop: "1px solid #555",
              }}
            >
              <button
                className="smrtcop-btn-action smrtcop-btn-cancel"
                style={{
                  flex: "none",
                  width: "auto",
                  padding: "0 12px",
                  height: 30,
                }}
                onClick={() => {
                  setRejectModal(null);
                  setRejectReason("");
                }}
              >
                Cancel
              </button>
              <button
                className="smrtcop-btn-action smrtcop-btn-warning"
                style={{
                  flex: "none",
                  width: "auto",
                  padding: "0 12px",
                  height: 30,
                }}
                disabled={!rejectReason.trim()}
                onClick={() => {
                  const key = rejectModal.key;
                  const reason = rejectReason.trim();
                  // Write the sidecar into whichever store the view reads from
                  // (submitted once the crew has submitted, otherwise the live
                  // answers), so the badge reflects the rejection either way.
                  const hasSubmission =
                    Object.keys(viewingSiteState.submitted || {}).length > 0;
                  const field = hasSubmission ? "submitted" : "answers";
                  const target = {
                    ...(viewingSiteState[field] || {}),
                    [`${key}_status`]: "fail",
                    [`${key}_statusReason`]: reason,
                  };
                  setViewingSiteState((s) => ({ ...s, [field]: target }));
                  // Persist only the two status sidecar keys. For a live (not yet
                  // submitted) site, crew may be editing concurrently, so we MERGE
                  // via answersPatch rather than replacing the whole answers map.
                  // A submitted site is frozen/admin-only, so a full replace of
                  // `submitted` is safe there.
                  const rejectBody = hasSubmission
                    ? { submitted: target }
                    : {
                        answersPatch: {
                          [`${key}_status`]: "fail",
                          [`${key}_statusReason`]: reason,
                        },
                      };
                  fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/smrtcop/site-state/${encodeURIComponent(viewingSite.id)}`,
                    {
                      method: "PUT",
                      credentials: "include",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(rejectBody),
                    },
                  ).catch((err) =>
                    console.error("Failed to save rejection:", err),
                  );
                  // Append to the permanent, org-wide rejection log (survives the
                  // crew's resubmit, which clears the live badge). The backend
                  // resolves who rejected + who it was sent to.
                  fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/smrtcop/rejections/${encodeURIComponent(viewingSite.id)}`,
                    {
                      method: "POST",
                      credentials: "include",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ fieldKey: key, reason }),
                    },
                  ).catch((err) =>
                    console.error("Failed to log rejection:", err),
                  );
                  setRejectModal(null);
                  setRejectReason("");
                }}
              >
                Send Back
              </button>
            </div>
          </div>
        </div>
      )}

      <SMRTCOPAssignCrewModal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setAssigningSiteId(null);
        }}
        onSubmit={handleAssignCrew}
        users={users}
        initialCrewLeadId={assignInitialLead}
        initialMemberIds={assignInitialMembers}
      />
      <SMRTCOPTrafficModal
        isOpen={isTrafficModalOpen}
        onClose={() => setIsTrafficModalOpen(false)}
        filter={trafficFilter}
        sites={sites}
        onViewSite={(siteId) => {
          const site = sites.find((s) => s.id === siteId);
          if (!site) return;
          if (!site.templateId) return;
          const tpl = templates.find((t) => t.id === site.templateId);
          if (!tpl) return;
          setViewingTemplate(tpl);
          setViewingSite(site);
        }}
      />
      <DeleteConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={deleteConfirm?.onConfirm || (() => {})}
        title={deleteConfirm?.title || ""}
        message={deleteConfirm?.message || ""}
        // A refusal has nothing to confirm, so the red Delete becomes a grey OK.
        // Same mapping ManageSMRTDocs uses, so the two read alike.
        confirmLabel={deleteConfirm?.blocked ? "OK" : "Delete"}
        confirmColor={deleteConfirm?.blocked ? "#4b5563" : undefined}
      />
      {renamingTabId !== null && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Rename Template</h2>
            </div>
            <div className="smrtdocs-modal-body">
              <input
                className="manage-input"
                value={renamingTabValue}
                autoFocus
                onChange={(e) => setRenamingTabValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const i = templates.findIndex(
                      (t) => t.id === renamingTabId,
                    );
                    if (i >= 0) saveTabRename(i);
                  }
                }}
                style={{ width: "100%", marginBottom: "20px" }}
              />
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  className="btn-cancel"
                  onClick={() => setRenamingTabId(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn-save"
                  onClick={() => {
                    const i = templates.findIndex(
                      (t) => t.id === renamingTabId,
                    );
                    if (i >= 0) saveTabRename(i);
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {verifyConfirm && (
        <div className="smrtdocs-modal-overlay" style={{ zIndex: 10000 }}>
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>
                {verifyConfirm.title}
              </h2>
            </div>
            <div className="smrtdocs-modal-body">
              <p
                style={{
                  fontSize: "0.9rem",
                  lineHeight: 1.5,
                  margin: "0 0 20px",
                }}
              >
                {verifyConfirm.message}
              </p>
              <div
                style={{
                  display: "flex",
                  justifyContent: verifyConfirm.infoOnly
                    ? "flex-end"
                    : "space-between",
                }}
              >
                {!verifyConfirm.infoOnly && (
                  <button
                    className="btn-cancel"
                    onClick={() => setVerifyConfirm(null)}
                  >
                    Cancel
                  </button>
                )}
                <button
                  className={verifyConfirm.infoOnly ? "btn-cancel" : "btn-save"}
                  onClick={verifyConfirm.onConfirm}
                >
                  {verifyConfirm.infoOnly ? "Back" : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
